import { shopDateTime } from '../../utils/regional';
import React,{useState, useEffect, useRef, useCallback, useContext, useMemo, Suspense, lazy} from 'react'
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import AddressComponent from './addressComponent';
import AddIcon from '@mui/icons-material/Add';
import AddOrEditaddressModal from './addOrEditAddressModal/AddOrEditAddressModal';
import CheckoutComponent from '../../cards/checkoutComponent'
import Registration from '../../buttons/registration/Registration'
import LoadingOrderModal from '../../modals/loadingOrderModal';
import OrderCompleteModal from '../../modals/orderCompleteModal';
import TipModal from '../../modals/tipModal';
import SmallAlert from '../../modals/smallAlert';
import AddressAutocomplete from '../address/AddressAutocomplete';
import AddressPinPicker from '../address/AddressPinPicker';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import loadGoogleMaps from '../../utils/mapsLoader';
import { extractAddress, matchTenantCity } from '../../utils/geo';
import {SlideDown} from 'react-slidedown';
import 'react-slidedown/lib/slidedown.css';
import { inject, observer } from "mobx-react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Colors from '../../assets/colors';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import GreenSubmitButton from '../../buttons/greenSubmitButton';
import RoundedButton from '../../buttons/roundedButton';
import SearchIcon from '@mui/icons-material/Search';
import "./Checkout.css";
import { DEFAULT_EMAIL, VIVA_SMART, VIVA_SOURCE, SITE_ID, STRIPE_PUBLISHABLE_KEY, API_URL } from "../../assets/globalConstants";
import Utils from '../../Utils';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toJS } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import * as uniq from 'lodash/uniq';
import * as flatten from 'lodash/flatten';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DateTime, Info } from "luxon";
import { OrderKind } from "../../utils/enums";
import { campaignTargetsCustomerClient, resolveCampaign, campaignsToMap, voucherValueFor } from "../../utils/campaignTargeting";
import { aadeMainActivity, aadeText, firstText } from "../../utils/aadeText";
import { storedCompanyContact } from "../../utils/storedCompanyContact";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import getUserLocale from 'get-user-locale';

// Translations
import _useTranslate from '../../hooks/_useTranslate';
import { values } from 'lodash';
import { ColorModeContext } from '../../theme/colorMode';
import { freeDeliveryReached, isServedCity } from '../../utils/deliveryFee';

const PayPalSection = React.memo(({ createOrder, onApprove, onClick, disabled }) => (
  <div className={`checkout-paypal ${disabled && "disable-paypal-buttons"}`} onClick={e => e.stopPropagation()}>
    <div className="checkout-paypal-buttons">
      <PayPalButtons
        disabled={disabled}   //the wrapper class is only pointer-events:none — CSS is not a payment gate
        createOrder={createOrder}
        onApprove={onApprove}
        onClick={onClick}
      />
    </div>
  </div>
));

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const StripeCardForm = React.memo(({ onPay, loading, disabled }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { isDark } = useContext(ColorModeContext);

  // Stripe's CardElement renders in a cross-origin iframe, so it can't read our
  // CSS variables — feed it concrete colors that follow the active light/dark mode.
  const cardOptions = useMemo(() => ({
    style: {
      base: {
        fontSize: '16px',
        color: isDark ? '#ececec' : '#424770',
        '::placeholder': { color: isDark ? '#8a8a8a' : '#aab7c4' },
      },
      invalid: { color: isDark ? '#ff6b6b' : '#9e2146' },
    },
  }), [isDark]);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    onPay(stripe, cardElement);
  };

  return (
    <div className="checkout-paypal">
      <div className="checkout-paypal-buttons" style={{ padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <CardElement options={cardOptions} />
        <GreenSubmitButton
          onClick={handlePay}
          loading={loading}
          aria-label='Stripe-pay-btn'
          width="100%"
          disabled={disabled || !stripe}
          title="Pay with Card"
          color="#635bff"
          style={{ marginTop: 16 }}
        />
      </div>
    </div>
  );
});

const Checkout = ({ feathersStore })=> {

  let navigate = useNavigate();
  let location = useLocation();

  let common = _useTranslate(feathersStore.language, "common");
  let checkout = _useTranslate(feathersStore.language, "checkout");
  // for account.myLocation, which is already translated in all 10 locales
  let account = _useTranslate(feathersStore.language, "account");

  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();

  const [formState, setFormState] = useState({
    paymentMethod: 'Cash',
    isValid: false
  });

  const paypalLocaleMap = {
    el: 'el_GR', en: 'en_GB', de: 'de_DE', es: 'es_ES',
    fr: 'fr_FR', it: 'it_IT', ar: 'ar_EG', bg: 'bg_BG',
    tr: 'tr_TR', sq: 'sq_AL',
  };

  useEffect(() => {
    if (feathersStore.settings?.PRODUCTION_CLIENT_ID) {
      const locale = paypalLocaleMap[feathersStore.language || feathersStore.settings.masterLanguage] ?? 'en_GB';
      paypalDispatch({
        type: 'resetOptions',
        value: { clientId: feathersStore.settings.PRODUCTION_CLIENT_ID, currency: feathersStore.settings.currency ?? 'EUR', intent: 'capture', locale },
      });
      paypalDispatch({ type: 'setLoadingStatus', value: 'pending' });
    }
  }, [feathersStore.settings?.PRODUCTION_CLIENT_ID, feathersStore.settings?.currency, feathersStore.settings?.masterLanguage, feathersStore.language]);
  
  const formRef = useRef();

  // Latest-ref mirrors for the memoized PayPal callbacks: createOrder/approved keep stable
  // identities across renders, so a direct computeTotals/sendOrder reference freezes at the
  // first render - where cartSub is still 0 - and PayPal creates/books "0.00" orders while
  // the cart is full. Reassigned every render right after the functions are defined.
  const computeTotalsRef = useRef();
  const sendOrderRef = useRef();

  const [origins, setOrigins] = useState([]);
  const [companyOrigins, setCompanyOrigins] = useState([]);
  const [cartSub, setCartSub] = useState(0);  
  const [isLoading, setLoading] = useState(false);
  const [liveTableItems, setLiveTableItems] = useState([]);
  const [isComplete, setComplete] = useState(false);  
  const [discount, setDiscount] = useState(0);
  const [voucherDiscount, setVoucherDiscount] = useState({});
  const [loyaltyReward, setLoyaltyReward] = useState(0);
  const [bonusGifts, setBonusGifts] = useState([]);
  const [vivaSmart, setVivaSmart] = useState(true);
  const [vivaPaymentOrdering, setVivaPaymentOrdering] = useState(false);
  const [tipModal, setTipModal] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [errorAlert, setErrorAlert] = useState({ show: false, message: '' });
  const showError = (message) => setErrorAlert({ show: true, message });
  const [isEshop, setIsEshop] = useState(() => {
    const itemStr = localStorage.getItem('tableId');
    // A missing/expired/corrupt key does NOT mean eshop while the store still holds a live
    // (age-capped) table session — TTL lapse / ITP purge mid-visit must never present the
    // eshop form to a seated customer. A VALID key always wins (a rescan in another tab).
    if (!itemStr) return !feathersStore.liveTableSessionValid();
    try {
      const item = JSON.parse(itemStr);
      if (new Date().getTime() > item.expiry) return !feathersStore.liveTableSessionValid();
      return item.value === 'eshop';
    } catch { return !feathersStore.liveTableSessionValid(); }
  });
  
  const companyIdRef = useRef();
  const filledFromRef = useRef();   //the record whose stored phone/email the form shows, if any
  let aadeResponseRef = useRef(null);
  const discountsArrayRef = useRef([]);
  //The rows the last sendOrder run added, so a re-run can take them back out before adding its own.
  const sendOrderRowsRef = useRef([]);
  const subscriptionIntervalsRef = useRef([1, 2, 3, 4, 5, 6, 12, 24]); 

  //---------company addresses controls
  const [addresses, setAddresses] = useState([]);  
  const [visibleAddNewAddress, setAddNewAddress] = useState(false);

  const openNewAddress = () => {     
    setAddNewAddress(true); 
  };

  const closeNewAddress = () =>{
    setAddNewAddress(false); 
  }  

  const setDeliveryAddress = item => () => {    
    const index = addresses.indexOf(item);
    let activeIndex = addresses.findIndex(e => e.active === true);
    if(activeIndex < 0)activeIndex = 0;        
    let clonedAddresses = [...addresses];

    if (activeIndex !== index) {
      clonedAddresses[activeIndex].active = false;
      clonedAddresses[index].active = true;      
      updateCompany(clonedAddresses);   
    }
  };

  const updateCompany = async (newAddresses) => {
    setAddresses([...newAddresses]);   
    await saveActiveAddress(newAddresses); 
  }

  const saveActiveAddress = async (val) => {   
    await feathersStore.patchCompanyAddresses( companyIdRef.current, [...val]);   
  };

  //-----------
  
  // JSON.parse(null) is null — NOT an empty array — so a visitor who lands on
  // /checkout with no cart yet (direct link, bookmark, cleared storage) used to
  // crash the page on the first cartItems.map (computeTotals + the summary rows).
  const cartItems = JSON.parse(localStorage.getItem('fdwa_cart')) || [];

  useEffect(()=> { //used for refresh
    // Same three-state resolution as cartTable (the two MUST agree): a VALID stored key always
    // wins — a rescan in another tab moved the customer to a different table and its id must be
    // adopted, not clobbered. A live (age-capped) in-memory session only beats a MISSING/EXPIRED
    // key (TTL lapse, iOS ITP purge): never flip a seated customer into the eshop form mid-visit.
    // Only a true session end (not in table mode) keeps the old expiry purge semantics.
    const stored = readStoredTableId();
    let resolvedTableId;
    if(stored.status === 'valid'){
      resolvedTableId = stored.value;
    }else if(feathersStore.liveTableSessionValid()){
      resolvedTableId = feathersStore.tableId;
    }else{
      if(stored.status === 'expired'){
        localStorage.removeItem('tableId');
        emptyCart();
      }
      resolvedTableId = 'eshop';
    }
    feathersStore.setTableId(resolvedTableId);
    feathersStore.touchTableSession(); //sliding 6h TTL while the table session is active
    if(feathersStore.isAuthenticated && resolvedTableId === 'eshop'){
      setIsEshop(true);
    } else if(resolvedTableId !== 'eshop'){
      // Confirmed table order — not an unauthenticated eshop user
      setIsEshop(false);
      formRef.current?.setFieldValue('isEshop', false);
      formRef.current?.setFieldValue('acceptedTerms', true);
    }
    // else: eshop user not yet authenticated — leave form untouched
  },[feathersStore.isAuthenticated]);

  useEffect(()=> {
    // Every required rule in the Yup schema keys on the `isEshop` HELPER FIELD, but the commit keys on
    // the LIVE feathersStore.tableId (sendOrder stamps webDelivery/webTakeAway from it). Those two used
    // to be able to diverge inside a single mount: the block above is the only writer of the form field
    // and it only ever writes FALSE — there is no setFieldValue('isEshop', true) anywhere in src — so a
    // table -> eshop flip with no remount (the booking-hour timer in Home.js, which deliberately keeps
    // the cart) left the helper stale false. With it false all nine required rules fall to their
    // `otherwise: notRequired` branch, errors === {} and isValid reads TRUE on a blank form, while the
    // commit still went out as an eshop order: ragou 2026-07-22 booked a webDelivery with every
    // identity and address field empty. Re-derive from the live mode, in BOTH directions, and
    // revalidate so isValid reflects the rules that actually apply now.
    const _isEshop = feathersStore.tableId === 'eshop';
    setIsEshop(_isEshop);
    // setValues(x, true) validates the EXACT object x (formik 2.4.9 setValues -> validateForm(resolvedValues)).
    // Two separate setFieldValue calls + a bare validateForm() would each validate the stale render
    // snapshot instead (setFieldValue validates setIn(state.values,...), validateForm() validates
    // state.values), so errors/isValid would stay pre-flip stale even though the values updated. Merge
    // both fields into the current values and validate once, so isValid reflects the mode that applies now.
    const cur = formRef.current;
    if(cur) cur.setValues({ ...cur.values, isEshop: _isEshop, acceptedTerms: _isEshop ? false : true }, true);
  },[feathersStore.tableId]);

  useEffect(()=> { //used for voucher general discount coming from Cart.js
    if(location?.state?.payload)setVoucherDiscount(location.state.payload[0]);
  },[location?.state?.payload]);

  //The router payload is a snapshot taken in the cart and it survives everything that happens on
  //this page - including a logout. The store is the authority, so when it drops to empty (identity
  //change, or the previous order committing) the voucher has to leave this screen with it.
  useEffect(()=> {
    if(feathersStore.discountsArray?.length === 0)setVoucherDiscount({});
  },[feathersStore.discountsArray]);

  useEffect(()=> { //Loyalty past rewards
      const _loyaltyReward = applyOldRewards();
      setLoyaltyReward(_loyaltyReward)
  },[feathersStore?.user]);

  useEffect(()=> { //earned Bonus gift(s) shown in the order summary (delivered as 0-priced product_free lines)
    const asyncFn = async() => {
      const rewards = (feathersStore.user?.loyaltyRewards || []).filter(r => !r?.applied && r?.bonus && r?.giftProductId);
      const gifts = [];
      for(const r of rewards){
        const p = await feathersStore.getProduct(r.giftProductId);
        if(p) gifts.push({ name: feathersStore._translateName(p), qty: +r.freeQty || 1 });
      }
      setBonusGifts(gifts);
    }
    feathersStore.user && asyncFn();
  },[feathersStore?.user]);

  const readStoredTableId = () => {
    const itemStr = localStorage.getItem('tableId');
    if(!itemStr) return { status: 'missing', value: null };
    try{
      const item = JSON.parse(itemStr);
      if(new Date().getTime() > item.expiry) return { status: 'expired', value: null };
      return { status: 'valid', value: item.value };
    }catch(e){ return { status: 'missing', value: null }; }
  }

  const emptyCart = () => {   
    localStorage.setItem("fdwa_cart", JSON.stringify([]));
    feathersStore.resetOrderItem();
    feathersStore.setCartLength(0);
  }

  useEffect(() => {
    //settings.VIVA_SMART (tenant doc, live-synced) wins over the deploy constant so one
    //server-side switch governs web + Android + iOS; the constant remains the fallback.
    setVivaSmart(feathersStore.settings?.VIVA_SMART ?? (VIVA_SMART || false));
    formRef.current.resetForm();
    formRef.current.validateForm();
  }, [VIVA_SMART, feathersStore.settings?.VIVA_SMART])

  useEffect(()=> {
    setOrigins(feathersStore.settings?.origins);
    setCompanyOrigins(feathersStore.settings?.companyOrigins);
    const defaultMethod = calculateDefaultPaymentMethod();
    formRef.current?.setFieldValue('paymentMethod', defaultMethod);
    setFormState(s => ({ ...s, paymentMethod: defaultMethod }));
    if (feathersStore.settings?.disableDelivery) {
      formRef.current?.setFieldValue('orderKind', 'ClickAway');
    } else {
      formRef.current?.setFieldValue('orderKind', 'Delivery');
    }
    formRef.current?.setFieldValue('reducedCheckoutData', feathersStore.settings?.reducedCheckoutData || false);
  // Two deps, NOT a boolean-AND: `[a && b]` evaluates to `false` for every guest, so the plant
  // effect ran exactly once at mount - usually before settings arrived - and the helper fields
  // (paymentMethod/orderKind/reducedCheckoutData) were stuck at their empty-settings values forever.
  },[feathersStore.isAuthenticated, feathersStore.settings]);

  useEffect(()=> {
    const _subTotal =  cartItems.map(item=> item.product_totalPrice)
      .reduce((prev, curr)=> +prev + +curr, 0).toFixed(2);
    setCartSub(_subTotal)
  },[cartItems])

    // DELETED login-prefill effect. It had been DEAD SINCE BIRTH: its guard read
    // `feathersStore.user?.firstname?.legth > 0` (typo), which is always false, so the whole body
    // - prefill AND its else-branch `resetForm()` - never executed once. Do NOT "fix" the typo to
    // revive it: replay-tested, the revived effect wipes the form for any logged-in named user
    // WITHOUT a stored address (the else pairs with the addresses check, so they fall into a bare
    // resetForm() on every dep change - unchecking acceptedTerms and re-blocking checkout), and
    // for users WITH an address it overwrites their typed edits on every cart/settings change.
    // Login prefill is already handled by initialValues (computed from feathersStore.user).

  //Eligibility is resolved ONCE, asynchronously, and the coupon path waits for it. `null` means
  //'not known yet' and is treated as NOT eligible, so a first-order coupon is never offered on a
  //guess. The server re-checks anyway - this only keeps the UI honest.
  const [firstOrderEligible, setFirstOrderEligible] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async() => {
      const eligible = await feathersStore.isFirstOrderEligible();
      if(!cancelled) setFirstOrderEligible(eligible);
    }
    run();
    return () => { cancelled = true; }
  },[feathersStore?.user?._id]);

  useEffect(()=> {
    const asyncFunction = async() => {    
      let couponsActive = [];
      if(feathersStore?.user?.coupons?.length > 0){
        couponsActive = feathersStore?.user?.coupons?.filter(cpn => cpn.userActive);
      }    
      if((couponsActive?.length > 0) && (cartSub > 0)){
        //Join the LIVE campaign onto each frozen per-user copy. Without this a campaign narrowed to
        //app-only (or deleted) would keep applying for everyone who already held it, because the
        //server hook that was meant to refresh the embedded copies is broken.
        let campaignsById = null;
        try{
          campaignsById = campaignsToMap(await feathersStore.coupons());
        }catch(error){
          campaignsById = null;   //fall back to the frozen copies rather than dropping every coupon
        }
        constructDiscount(couponsActive, campaignsById);
      }
    }
    asyncFunction();
  },[feathersStore?.user?.coupons, cartSub, feathersStore.tableId, firstOrderEligible]);

  //----> O&P: keep a live, read-only copy of what is ALREADY on the table (others' items),
  // refreshed when the table is patched from the dashboard/PDA (tablePatchedEvent, guarded to this table).
  useEffect(() => {
    if(!feathersStore.isAuthenticated) return; //wait for the feathers client to connect
    if(feathersStore.tableId === 'eshop' || !feathersStore.tableId){ setLiveTableItems([]); return; }
    const evt = feathersStore.tablePatchedEvent;
    if(evt?._id === feathersStore.tableId && evt?.order){
      setLiveTableItems(evt.order.items || []);
      return;
    }
    let cancelled = false;
    (async () => {
      try{
        const table = await feathersStore.getTable(feathersStore.tableId);
        if(!cancelled && table) setLiveTableItems(table?.order?.items || []);
      }catch(e){ console.log("liveTable fetch error", e); }
    })();
    return () => { cancelled = true; };
  },[feathersStore.isAuthenticated, feathersStore.tableId, feathersStore.tablePatchedEvent]);
 
  const constructTitle = (item)=> {  
    return feathersStore._translate(item.product_name , item.product_nameTranslated) 
    + (item?.product_size?.name ? 
      "  " + feathersStore._translateNameAttr(item.product_size) :
      "");     
  }  

  const _isPaidItem = item => {
    const q = +item?.product_quantity || 0;
    const pq = +item?.paid_quantity || 0;
    return item?.paid === true || (q > 0 && pq >= q);
  }
  const _isPartiallyPaidItem = item => {
    const q = +item?.product_quantity || 0;
    const pq = +item?.paid_quantity || 0;
    return !item?.paid && pq > 0 && pq < q;
  }

  const renderExistingTableItems = liveTableItems.map((item, index) => {
    const paid = _isPaidItem(item);
    const partial = _isPartiallyPaidItem(item);
    return (
      <div key={`existing-${index}`} className="checkout-order-table-header" style={{opacity: 0.6}}>
        <span style={{textDecoration: paid ? 'line-through' : 'none'}}>
          {item.product_quantity} x {constructTitle(item)}
        </span>
        <span style={{whiteSpace: 'nowrap'}}>
          <span style={{
            fontSize: '0.7rem', fontWeight: 700, marginRight: 8, padding: '4px 12px', borderRadius: 10, display: 'inline-block',
            color: '#fff', backgroundColor: paid ? '#4caf50' : (partial ? '#ff9800' : '#9e9e9e')
          }}>
            {paid ? (checkout.paidLabel || "Paid") : (partial ? (checkout.partiallyPaidLabel || "Part. paid") : (checkout.unpaidLabel || "Unpaid"))}
          </span>
          {feathersStore.formatMoney((+item.product_totalPrice || 0).toFixed(2))}
        </span>
      </div>
    );
  });

  const constructData = item => {
    return (item?.product_weightedQuantity > 0 ? ((+item.product_weightedQuantity * 1000).toString() + " " + common.grams + " ") : "")
    + item.features?.map(feature => feathersStore. _translateNameAttr(feature) + "  ")
    + item.attributes?.map(attribute => "  " + feathersStore._translateTitle(attribute) + ": " 
        + feathersStore. _translateNameAttr(attribute) + " " +(attribute?.quantity > 1 ? "x" + attribute.quantity + " " : ""))
    + (item?.ingredients?.length > 0 ? ("  " + checkout.without  + item.ingredients?.map(ingredient => "  " 
        + feathersStore. _translateNameAttr(ingredient))) : "")
  }

  const BanksIterator = () => {
    return(
      <table className="banks-container">
        <thead>
          <tr>
            <th>{checkout.bank}</th>
            <th>IBAN</th>
          </tr>       
        </thead>
        <tbody>       
          {feathersStore.settings?.bankAccounts
            ?.filter(item => item.bankEnabled)
            ?.map((bank, index) => 
              <tr key={bank.bankIBAN}>
                <td>
                  {bank.bankName}
                </td> 
                <td>
                  {bank.bankIBAN}
                </td>
              </tr> 
          )}  
        </tbody>     
      </table>
    )}

  //-----> Viva payment order START

  const closeTipModal = () => {
    setTipModal(false);
  }

  const applyTipAmount = async (tipAmount) => {    
    closeTipModal();
    await _createVivaOrder(tipAmount);
  }

  const createVivaOrder = async () => {
    setTipModal(true);
  }
  
  const _createVivaOrder = async (tipAmount = 0) => {
    try{
      // Viva was the weakest rail: no JS check at all, only the button's disabled prop. Gate here,
      // before the payment order is created and before the redirect.
      if(await identityGap()){
        showError(checkout.fillAllFields || "Please fill in all required fields.");
        return;
      }
      setVivaPaymentOrdering(true);
      let customerTrns = "";
      for (let item of cartItems){
        customerTrns += constructTitle(item);
      }
      customerTrns = customerTrns.slice(0, 2048);
      // Non-mutating snapshot: Object.assign onto formRef.current.values grafted keys through the
      // resetForm alias into formik's initialValues.current - the second poisoner (see computeTotals).
      const values = { ...formRef.current.values, ...computeTotals(formRef.current.values), tipAmount, customerTrns };
      // AWAITED and honored: fire-and-forget here meant a couver-gate bounce could not stop the
      // payment (the redirect to VivaPage still happened) and fdwa_orderItem could be persisted
      // stale before replaceOrderItem ran. Abort the whole payment flow if the order was not prepared.
      const prepared = await sendOrder(values, true);
      if(!prepared){ setVivaPaymentOrdering(false); return; }

      const SETTINGS_COUNTRY_MAP = { el: 'GR', gr: 'GR', uk: 'GB', gb: 'GB', us: 'US', cy: 'CY' };
      const merchantCountry = SETTINGS_COUNTRY_MAP[(feathersStore.settings?.country || 'el').toLowerCase()] || 'GR';
      const localeMatch = (getUserLocale({fallbackLocale: ''}) || '').match(/-([A-Za-z]{2})$/);

      const paymentOrder = {
        amount: Math.round((+values.grandTotal + +tipAmount) * 100) ,  //in cents
        customerTrns, //short description of items purchased shown to customer. Up to 2048 characters.
        dynamicDescriptor: feathersStore.cardDescriptor(),  //up to 13 characters, presented in buyer bank statement
        tipAmount: Math.round(tipAmount * 100),
        merchantTrns: customerTrns,  //Short description of items/services purchased by customer for us in viva app to find the transaction,
        customerEmail: values?.email,
        customerFullName: values?.isCompany ? values?.legalName : (values?.firstName || "") + (values?.lastName || ""),
        phone: values?.phone || values?.companyPhone || values?.deliveryPhone,
        countryCode: localeMatch ? localeMatch[1].toUpperCase() : merchantCountry, //important - affects payment methods
        requestLang: languageCode() ?? (feathersStore.settings?.masterLanguage === 'el' ? 'el-GR' : 'en-GB'), //important - affects language of viva page
        allowRecurring: values?.allowRecurring || false,
        siteId: SITE_ID, //server resolves the Viva sourceCode from connectedSites by this id (empty => legacy/source fallback)
        source: VIVA_SOURCE //multisource: server maps this to the right Viva source code (empty => legacy VIVA_SMART_SOURCECODE)
      }

      const response = await feathersStore.createPaymentOrder(paymentOrder);
      const {orderCode} = response;
      feathersStore.setOrderCode(orderCode);
      feathersStore.saveStateToLocalStorage();
      setVivaPaymentOrdering(false);
      navigate("/VivaPage") ;       
    }catch(error){
      console.log(error);
      setVivaPaymentOrdering(false); //otherwise the pay button spins forever after a sendOrder/createPaymentOrder throw
      showError(checkout.paymentError || "Payment failed. Please try again.");
    }
  }

  const languageCode = () => {
    switch (feathersStore.language){
      case "el" : {
          return 'el-GR';
      }
      case "en" : {
          return 'en-GB';
      }
      case "de" : {
          return 'de-DE';
      }
      case "es" : {
          return 'es-ES';
      }
      case "fr" : {
          return 'fr-FR';
      }
      case "it" : {
          return 'it-IT';
      }
      case "sq" : {
        return 'sq-AL';
      }
      case "ar" : {
        return 'ar-AR';
      }
      case "tr" : {
        return 'tr-TR';
      }
      case "bg" : {
        return 'bg-BG';
      }
    }
  }

  //-----> Viva payment order END

  //-----> PAYPAL START

  const createOrder = useCallback((data, actions) => {
    const values = { ...(formRef.current?.values || {}), ...computeTotalsRef.current(formRef.current?.values || {}) };
    return actions.order
      .create({
        purchase_units: [
          {
            amount: {
              currency_code: feathersStore.settings?.currency ?? 'EUR',
              value: parseFloat(values?.grandTotal).toFixed(2),
            },
          },
        ],
        payer: {
          email_address: values?.email || undefined,
          name: {
            given_name: values?.firstName || undefined,
            surname: values?.lastName || undefined,
          },
        },
      })
      .then((orderID) => {
        feathersStore.setOrderItem({paypalOrderID: orderID});
        return orderID;
      })
      .catch((err) => {
        console.error('PayPal createOrder error:', err);
      });
  }, [feathersStore.settings?.currency]);

  const approved = useCallback((data, actions) => {
    return actions.order.capture().then(function(details) {
      // Snapshot at COMMIT time, through the live mirrors: this deps-[] callback outlives every
      // render, and the first-render computeTotals/sendOrder it used to capture had cartSub 0 -
      // orders were booked with grandTotal "0.00" against a full cart (coast, Aug 7-13).
      const values = { ...(formRef.current?.values || {}), ...computeTotalsRef.current(formRef.current?.values || {}) };
      // Carry the capture id through: it is what proves the money moved, and it keys the ledger row.
      const captureId = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id
        || details?.id || data?.orderID;
      return sendOrderRef.current(values, false, true, captureId); //charged: the capture above already took the money
    });
  }, []);
  
  const onInit = (data, actions) =>  {
    let formikRef;
    if (formRef.current) {
      formikRef = formRef.current;
    }

    // Disable the buttons
    actions.disable();
    if (formikRef.isValid)  {
      actions.enable();
      } else  {
      actions.disable();
    }
  }

  const onClick = useCallback(async (data, actions) => {
    if (await identityGap()) {   //re-validates; isValid alone can be a stale true
      showError(checkout.fillAllFields || "Please fill in all required fields.");
      return actions.reject();
    }
    // PayPal captures inside approved() BEFORE sendOrder runs its gate — so gate here, pre-charge.
    if(!(await passesCouverGate())){
      navigate('/cart');
      return actions.reject();
    }
  }, []);

  //-----PAYPAL END

  //-----> STRIPE START

  const handleStripePay = async (stripe, cardElement) => {
    const values = { ...(formRef.current?.values || {}), ...computeTotals(formRef.current?.values || {}) };
    if (await identityGap()) {   //re-validates; isValid alone can be a stale true
      showError(checkout.fillAllFields || "Please fill in all required fields.");
      return;
    }
    try {
      setStripeLoading(true);
      // Stripe confirms the charge BEFORE sendOrder runs its gate — so gate here, pre-charge.
      if(!(await passesCouverGate())){
        setStripeLoading(false);
        navigate('/cart');
        return;
      }
      const grandTotal = parseFloat(values?.grandTotal);
      const amount = Math.round(grandTotal * 100);
      if (isNaN(amount) || amount <= 0) {
        showError(checkout.invalidTotal || "Invalid order total. Please review your cart.");
        setStripeLoading(false);
        return;
      }
      const payload = {
        total: grandTotal,
        currency: (feathersStore.settings?.currency ?? 'EUR').toLowerCase(),
        description: `Order from ${values.firstName} ${values.lastName}`,
      };
      const stripeResponse = await feathersStore.createStripe(payload);
      const clientSecret = stripeResponse.clientSecret || stripeResponse.client_secret;
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${values.firstName} ${values.lastName}`,
          },
        },
      });
      if (result.error) {
        showError(result.error.message);
      } else if (result.paymentIntent?.status === 'succeeded') {
        //charged: the confirmed PaymentIntent already took the money; its id keys the ledger row
        await sendOrder(values, false, true, result.paymentIntent.id);
      }
    } catch (error) {
      console.log(error);
      showError(checkout.paymentError || "Payment failed. Please try again.");
    } finally {
      setStripeLoading(false);
    }
  };

  //-----STRIPE END

  //----------> LOYALTY

  const applyOldRewards = () => {//applied at the beginning of sendOrder
    let totalReward = 0;
    if(feathersStore?.user?.email !== DEFAULT_EMAIL){
      if(feathersStore.user?.loyaltyRewards?.length > 0){
        totalReward = feathersStore.user?.loyaltyRewards
          ?.filter(r => !r?.applied)//Old rewards not yet applied
          ?.map(reward => reward?.value || 0)
          ?.reduce((a, b) => +a + +b, 0);       
      }
    }
    return totalReward;
  }

  //-------------->

  //----> COUVER GATE (table mode). Standalone so EVERY payment rail can consult it BEFORE money
  // moves (PayPal onClick, Stripe handleStripePay, Viva via the awaited sendOrder) — the card rails
  // used to charge first and run the gate after. Fail-closed on every uncertain answer:
  // - settings not loaded yet (hard /checkout reload before the socket round-trip) used to skip the
  //   old inline gate entirely via its own `settings?.couver` condition;
  // - a booking PRE-ORDER must carry its own couver — the live table may hold ANOTHER party's, and
  //   the pre-order is deliberately built without the live order (see the append comment below).
  const passesCouverGate = async () => {
    if(feathersStore.tableId === 'eshop') return true;
    const s = feathersStore.settings;
    if(!s || Object.keys(s).length === 0) return false;   //settings not loaded yet
    if(!s.couver) return true;
    try{
      const _couverId = s.couverId;
      const _storedCart = JSON.parse(localStorage.getItem('fdwa_cart') || '[]');
      if(Array.isArray(_storedCart) && _storedCart.some(i => i.product_id === _couverId)) return true;
      if(feathersStore.preOrderBooking?.tableId === feathersStore.tableId) return false;
      const _liveTable = await feathersStore.getTable(feathersStore.tableId);
      if(_liveTable?.order?.items?.some(i => i.product_id === _couverId)) return true;
      // couverOnce table with a missing couver: nothing needs the patron's attention (quantity is
      // always 1), so let the send proceed - the commit-time heal injects the couver line. Bouncing
      // here silently wiped a fully-filled checkout form ("it kicks me back to the cart", coast
      // 2026-07-07) for no reason. Multi-person tables still bounce: the cart's persons modal must
      // collect the couver quantity.
      return !!_liveTable?.couverOnce;
    }catch(e){ return false; }
  };
  //<---- COUVER GATE END

  //----> IDENTITY GATE (eshop mode only). Standalone so EVERY rail can consult it BEFORE money moves.
  // Deliberately does NOT trust formik.isValid: that is just `Object.keys(errors).length === 0`, which
  // reads true on a form that has never been validated (there is no validateOnMount) AND on a form whose
  // schema was disarmed by a stale isEshop helper. So this forces a fresh validateForm() AND reads the
  // values directly — no snapshot gets to vouch for a blank order. Returns the first offending field
  // name, or null when the order is fine. Table / O&P returns null BY DESIGN: a seated QR customer
  // never fills a form and must keep name/phone/address optional.
  const identityGap = async () => {
    if(feathersStore.tableId !== 'eshop') return null;
    const v = formRef.current?.values || {};
    const blank = ['firstName', 'lastName'].filter(k => !`${v[k] ?? ''}`.trim());
    if(!feathersStore.settings?.reducedCheckoutData){
      //same predicate the Yup rules use: required only when the tenant has not opted into the
      //reduced checkout form. Contact details are needed however the order is handed over - the
      //shop still has to reach the customer for a collection.
      ['phone', 'email'].forEach(k => { if(!`${v[k] ?? ''}`.trim()) blank.push(k); });
      //ADDRESS: Delivery only, matching the Yup rules above and the mobile apps. This gate MUST be
      //kept in lockstep with them - it is a second copy of the same predicate, and if it kept
      //demanding an address the collection order would pass validation (button enabled) and then be
      //refused here by a toast, which is worse than the disabled button it replaced.
      if(v.orderKind === "Delivery"){
        ['street', 'streetNumber'].forEach(k => { if(!`${v[k] ?? ''}`.trim()) blank.push(k); });
        if(!(+v.city > 0)) blank.push('city');   //city is a numeric id, -1 when unchosen
      }
    }
    // Validate a CORRECTED object: the Yup when() rules key on the isEshop/reducedCheckoutData
    // HELPER FORM FIELDS, which are plantable/wipable state (a reinit or a missed plant leaves them
    // stale - for a guest the plant effect's old boolean-AND dep array ran exactly once at mount).
    // formik 2.4.9 validateForm(values) validates the object you pass it, so hand it the live
    // truth and no stale helper can either disarm the rules (ragou) or arm the six hidden
    // address rules on a reduced-checkout form (invisible permanent block).
    const errs = await formRef.current?.validateForm({ ...v,
      isEshop: feathersStore.tableId === 'eshop',
      reducedCheckoutData: !!feathersStore.settings?.reducedCheckoutData });
    return blank[0] || Object.keys(errs || {})[0] || null;
  };
  //<---- IDENTITY GATE END

  // chargeId: the processor's own id for a capture that has ALREADY succeeded (PayPal capture id /
  // Stripe PaymentIntent id). It is what turns `charged` from a claim into a ledger row - see the
  // paidBy mint in the table branch below.
  const sendOrder = async (values, eshop, charged, chargeId) => {

    // Authoritative totals at the commit chokepoint: the summary numbers are no longer assigned
    // into formik.values during render (see computeTotals), so merge them here for every rail -
    // Cash/BankTransfer arrive via Formik onSubmit with raw values, card rails pass snapshots.
    values = { ...values, ...computeTotals(values) };

    // Send-time enforcement: on bounce the customer lands on /cart where the existing flow adds the
    // couver - auto for a couverOnce (one-person) table, the quantity modal for a multi-person table.
    // Placed BEFORE any discountsArrayRef mutation so the bounce/retry cannot double-apply
    // loyalty/voucher. Returns false so async callers (Viva) can abort their payment flow.
    // charged=true (PayPal capture / Stripe confirm already succeeded): NEVER bounce a paid order -
    // committing it is strictly better than losing it, and the commit heal injects a missing couver.
    if(!charged && !(await passesCouverGate())){ navigate('/cart'); return false; }

    // Identity gate at the single commit chokepoint. Cash/BankTransfer reach here through Formik's
    // onSubmit, which revalidates against the SAME schema — so they need a check that does not trust
    // that schema either. charged=true should now be unreachable (every rail gates pre-charge), but if
    // it ever fires we COMMIT AND MARK rather than lose a paid order: a charged-but-no-order ghost is
    // strictly worse than an incomplete record the tenant can chase (2026-07-01 ghost receipt).
    const _identityGap = await identityGap();
    if(_identityGap && !charged){
      showError(checkout.fillAllFields || "Please fill in all required fields.");
      return false;
    }

    //Loyalty
    //`values` was merged with computeTotals(values) a few lines up, so these are the CAPPED figures.
    const _loyaltyApplied = +values.loyaltyDiscount || 0;
    const _voucherApplied = +values.couponDiscount || 0;
    //sendOrder can run twice in one checkout (a failed commit retried, a Viva order-create failure,
    //a double tap). Pushing again stacked a second Loyalty/voucher row on the first: the bill took
    //both off, and addNewRewards burned the loyalty balance twice. Drop the last run's rows first.
    discountsArrayRef.current = discountsArrayRef.current.filter(d => !sendOrderRowsRef.current.includes(d));
    sendOrderRowsRef.current = [];
    if(_loyaltyApplied > 0)sendOrderRowsRef.current.push({
      type : "Cash",
      label : "Loyalty",
      value : parse_fix(_loyaltyApplied),   //the CAPPED figure, not the reward's face value
      reward : parse_fix(loyaltyReward),    //face value kept, so the record shows what was earned
      applied : false,
      promotionText : "Loyalty Rewards"
    }) 

    //voucher - value capped the same way; `reward` (the % or cash the campaign declares) is untouched
    if(_voucherApplied > 0)sendOrderRowsRef.current.push({...voucherDiscount, value : parse_fix(_voucherApplied)}) 
    discountsArrayRef.current.push(...sendOrderRowsRef.current);

    const discounts = discountsArrayRef.current
      ?.filter(disc => !disc?.applied)
      ?.map(d => d.value)
      ?.reduce((a,b) => +a + +b, 0);   

    const day = new Date().getDate(); //Current Date
    const month = new Date().getMonth() + 1; //Current Month
    const year = new Date().getFullYear(); //Current Year
    const hours = new Date().getHours(); //Current Hours
    const min = new Date().getMinutes(); //Current Minutes
    const initDate = day + '/' + month + '/' + year + ' ' + hours + ':' + min;
    let finalOrderItems = [...cartItems];
    finalOrderItems.forEach(item => {
      Object.assign(item, {
        createdAt: new Date(),
        receipt: false
      });
      return item;
    })
    // Gate the paid-marks on the CHARGE, not on the selected radio. Stamping them off the radio
    // marked every line "paid" before a single euro moved: on the Viva rail sendOrder runs BEFORE
    // the redirect, so an abandoned payment left a table whose items all read paid while the full
    // amount was still owed - and tables.js:233-238 then refuses to re-take it ("Item already
    // paid"), which is why those tables could never be closed. success.js does the same stamp on
    // the way back from a REAL Viva payment, so the paid rail is unaffected.
    // ESHOP keeps the old radio-based behaviour: its order is written once through createOrder with
    // a client-set unpaid, its items are not marked _webNew, and success.js only re-stamps _webNew
    // items - so gating eshop on `charged` would silently drop the paid marks on the whole Viva
    // cohort. Only TABLE orders move to the charge-based test, which is where the damage was.
    if(charged || (feathersStore.tableId === 'eshop' && values.paymentMethod === 'PayPal')){
      finalOrderItems.forEach(item => {
        Object.assign(item, {
          'paid_quantity': item.product_quantity,
          'visa_quantity': item.product_quantity
        });
        return item;
      })
    }
    //----> BONUS redemption: append earned free items (product_free, 0-priced) for each unapplied
    // Bonus reward. Injected after the cart spread & after the subtotal/total were computed, so the
    // free gift never affects subtotal/total/minimum/transport. Marked applied by addNewRewards after the order.
    const _bonusRewards = (feathersStore.user?.loyaltyRewards || []).filter(r => !r?.applied && r?.bonus && r?.giftProductId);
    for(const reward of _bonusRewards){
      try{
        const giftProduct = await feathersStore.getProduct(reward.giftProductId);
        if(!giftProduct) continue;
        const pickedSize = giftProduct.sizes?.find(s => s?.default);
        finalOrderItems.push({
          id: finalOrderItems.reduce((m, i) => Math.max(m, +i.id || 0), -1) + 1,
          createdAt: new Date(),
          receipt: false,
          product_id: giftProduct._id,
          product_name: giftProduct.name,
          product_nameTranslated: feathersStore._translateName(giftProduct),
          product_size: pickedSize || {},
          product_image: `${API_URL}/images/${giftProduct.images?.[0] || ""}`,
          product_price: 0,
          product_totalPrice: 0,
          product_netPrice: 0,
          product_quantity: +reward.freeQty || 1,
          product_extraSum: 0,
          features: [], ingredients: [], attributes: [],
          product_printers: giftProduct.printers || [],
          product_blockOtherPrinters: false,
          product_kdsSections: giftProduct.kdsSections || [],
          product_vat: giftProduct.vat || 0,
          product_stock: feathersStore.effectiveStock(giftProduct, pickedSize),
          product_handleStock: giftProduct.handleStock || false,
          product_comments: "",
          product_weightedPrice: 0,
          product_weightedQuantity: 0,
          product_recipes: pickedSize?.recipes?.length ? pickedSize.recipes : (giftProduct.recipes ?? []),
          nameTranslations: giftProduct.nameTranslations || [],
          shortDescriptionTranslations: giftProduct.shortDescriptionTranslations || [],
          product_free: true,
          //Parity with the RN apps. Without this the web had NOTHING correlating a shipped gift line
          //back to the reward that paid for it, so addNewRewards could only burn blindly - and did.
          _bonusRewardCode: reward.code //so addNewRewards burns this reward only once the gift is actually delivered
        });
      }catch(e){ console.log('Could not build bonus gift line', e); }
    }
    //<---- BONUS redemption END
    let dataItem = {
      'bookingDate': initDate,
      'processed': false,   
      'comments':values.comments || "",
      'firstName': values.firstName,
      'lastName': values.lastName,
      'street': values.street,
      'streetNumber': values.streetNumber,
      'city': findCity(values.city),
      'origin': values.origin, 
      'apartment' : "", 
      'postcode': values?.postcode || "",         
      'phone': values.phone,
      'email': values.email, 
      // Exact delivery coordinates when the customer picked an autocomplete
      // suggestion (or saved an address that had been picked that way);
      // 0 otherwise, which is the historic value for every web order.
      'latitude': +values.latitude || 0,
      'longitude': +values.longitude || 0,
      'isCompany': values.isCompany,
      'afm': values.isCompany ? values.afm : "",
      'legalName': values.isCompany ? values.legalName : "",
      'legalDescription': values.isCompany ? values.legalDescription : "",
      'doyDescription': values.isCompany ? values.doyDescription : "",
      'firmActDescription': values.isCompany ? values.firmActDescription : "",
      'postalAddress': values.isCompany ? values.postalAddress : "",
      'postalAddressNo': values.isCompany ? values.postalAddressNo : "",
      'postalAreaDescription': values.isCompany ? values.postalAreaDescription : "",
      'postalZipCode': values.isCompany ? values.postalZipCode : "",
      'companyPhone': values.isCompany ? values.companyPhone : "",
      'companyEmail': values.isCompany ? values.companyEmail : "",
      'companyOrigin': values.isCompany ? values.companyOrigin : 1, 
      'hasOtherAddress': values.hasOtherAddress,
      'deliveryFirstName': values.hasOtherAddress ? values.deliveryFirstName : "",
      'deliveryLastName': values.hasOtherAddress ? values.deliveryLastName : "",
      'deliveryStreet': values.hasOtherAddress ? values.deliveryStreet : "",
      'deliveryStreetNumber': values.hasOtherAddress ? values.deliveryStreetNumber : "",
      'deliveryCity': values.hasOtherAddress ? findCity(values.deliveryCity) : "", 
      'deliveryOrigin': values.hasOtherAddress ? values.deliveryOrigin : 1, 
      'deliveryPostcode': values.hasOtherAddress ? values?.deliveryPostcode : "", 
      'deliveryPhone': values.hasOtherAddress ? values.deliveryPhone : "",   
    }
    if(feathersStore.tableId === 'eshop'){
      Object.assign(dataItem, { tipAmount: values?.tipAmount || 0})
      if(values.deferredOrder && values.deferredDate && values.deferredTime){
        console.log("VOOO")
        const deferredDateTime = shopDateTime(values.deferredDate, values.deferredTime, feathersStore.timezone()).toISO();
        Object.assign(dataItem, { deferredOrder: true, deferredTime: deferredDateTime });
      }
    }
    if(values.isCompany){
      const companyDeliveryAddress = addresses.find(a => a.active);
      Object.assign(dataItem, {companyDeliveryAddress});
      await saveOrUpdateCompany(dataItem.companyPhone, dataItem.companyEmail, dataItem.companyOrigin, values.afm);
    }
    if(feathersStore.tableId !== 'eshop'){
      const table = await feathersStore.getTable(feathersStore.tableId);
      let oldOrderClone = toJS(table?.order ?? {});
      const printersUsed = uniq(flatten(finalOrderItems.map(item => item?.product_kdsSections?.map(prn => prn.name)))).filter(Boolean)
      Object.assign(dataItem, {
        '_id': table.tableName,
        'waiterId': "qrcode",
        'waiterUsername': "qrcode",
        'customerId': "anonymous", 
        "tableId": feathersStore.tableId,
        "orderKind": OrderKind.webTable,
        'paymentMethod': values.paymentMethod, 
      })    
      //append order — but a booking PRE-ORDER is independent of whatever order currently sits on the
      // (possibly occupied) table: build it as a fresh order and never merge the live one in, so no
      // other diner's numericId / subOrders / activeUid / KDS flags leak into the stored pre-order.
      // The test is the SAME one loadingOrderModal uses to take the pre-order path, not a bare
      // !preOrderBooking: with a booking held for a DIFFERENT table than the one being ordered from,
      // the two disagree — the merge would be skipped while the ordinary append still runs, so the
      // live order's activeUid would not be inherited and the mint at loadingOrderModal.js would
      // overwrite it, orphaning any card payment already taken against that table's session.
      if(feathersStore.preOrderBooking?.tableId !== feathersStore.tableId &&
        table?.order && table.order?.items?.length > 0){
        const maxId = calculateMaxId(table.order);
        finalOrderItems.forEach((item, index) => {
          // _clientItemKey is minted HERE, once per line of THIS checkout submission, and rides in
          // the stored orderItem through the Viva redirect and the success-page reload - so a retry
          // re-sends identical keys and the server credits each line once. It must NOT be derived
          // from activeUid (as the commit used to do): activeUid persists for the whole table
          // session, so a SECOND round's positional -i0 collided with the first round's -i0 and the
          // server dropped it as a duplicate - silently LOSING a paid item.
          Object.assign(item, {reorder: true, id: +maxId + 1 + +index, _webNew: true, _clientItemKey: `web-${uuidv4()}`})
          return item;
        })
        Object.assign(dataItem, table.order, {
        'items':  [...oldOrderClone.items, ...finalOrderItems],
        'discounts': +discounts,
        discountsArray: discountsArrayRef.current,
        // Re-assert THIS submission's rail. `table.order` is spread above, so without this line the
        // live order's paymentMethod wins and a table whose earlier round was paid by card labels
        // every later round 'PayPal' - including cash ones. It then persists, because the commit
        // sends update.paymentMethod (loadingOrderModal) and `update` beats the stored order
        // server-side (paymentMethod is not in PROTECTED_ORDER_FIELDS). The Android sibling has
        // always had this line (FinalData.js:718); the web app never received the port.
        'paymentMethod': values.paymentMethod,
        'total': +oldOrderClone.total + +values.grandTotal,
        'unpaid': +oldOrderClone.unpaid + (values.paymentMethod === 'PayPal' ? 0 : +values.grandTotal),
        '_webTotalDelta': +values.grandTotal,
        '_webUnpaidDelta': (values.paymentMethod === 'PayPal' ? 0 : +values.grandTotal)
        })
      }else{
          //new order
        finalOrderItems?.forEach((item, index) => {
          //same per-line key as the append branch above - see the note there
          Object.assign(item, {id: +index, _webNew: true, _clientItemKey: `web-${uuidv4()}`})
          return item;
        })
        Object.assign(dataItem, {
          'items': [...finalOrderItems],
          'discounts': +discounts,
          discountsArray: discountsArrayRef.current,
          'total': +values.grandTotal,
          "unpaid": (values.paymentMethod === 'PayPal' ? 0 : +values.grandTotal),
          'status': "Pending",
          '_webTotalDelta': +values.grandTotal,
          '_webUnpaidDelta': (values.paymentMethod === 'PayPal' ? 0 : +values.grandTotal)
        })
        printersUsed?.forEach(printer => {
          Object.assign(dataItem, {[`${printer}`]: "Pending"})
        })
      }
      // A captured card charge MUST become a ledger row. The server derives
      // unpaid = total - sum(paidBy.paidSum) (tables.js:264-265) and DISCARDS any client-sent
      // unpaid (PROTECTED_ORDER_FIELDS, tables.js:69-70), so a rail that takes money without
      // writing paidBy leaves the table owing the full amount for ever. success.js does this for
      // the Viva redirect; the PayPal and Stripe rails never did - they were the only rails whose
      // captures returned straight here, so their money never reached the balance.
      // Keyed off THIS charge, never off activeUid: activeUid lives for the whole table session,
      // so a second payment re-sent the first one's key and the server dedup dropped it.
      if(charged && chargeId){
        Object.assign(dataItem, {
          paidBy: [...(dataItem.paidBy || []), {
            waiterId: dataItem.orderKind,
            branch: feathersStore?.selectedBranch,
            paidSum: +values.grandTotal,
            method: "Visa",
            createdAt: new Date().getTime(),
            _clientPayKey: `${chargeId}-pb`,
            _webNew: true
          }],
          payments: [...(dataItem.payments || []), {
            PaymentMethodType: "Card",
            PaymentMethodTypeCode: "7",
            PaymentAmount: +values.grandTotal,
            PaymentNotes: "Card",
            transactionId: chargeId,
            tipAmount: dataItem?.tipAmount || 0,
            _webNew: true
          }]
        })
      }
    }else{
      Object.assign(dataItem, {
        //only ever set on the charged-but-incomplete escape hatch above, so the tenant can spot an
        //order they cannot deliver instead of discovering it at the door
        ...(_identityGap ? {'_identityMissing': _identityGap} : {}),
        'customerId': feathersStore?.user._id || "anonymous_mobile_customer",
        "unpaid": (values.paymentMethod === 'PayPal' ? 0 : +values.grandTotal),
        'items': [...finalOrderItems],
        'transportation': +values.transportation,
        'transportationPaid': (+values.transportation > 0) && (values.paymentMethod === 'PayPal') ? true : false,
        'discounts': +discounts,
        discountsArray: discountsArrayRef.current,
        'total': +values.grandTotal,
        'status': 'Pending',
        'paymentMethod': values.paymentMethod,
        'orderKind': values.orderKind === 'ClickAway' ? OrderKind.webTakeAway : OrderKind.webDelivery
      });
    }       
    feathersStore.replaceOrderItem(dataItem);
    if(!eshop){
      setLoading(true);
    }
    return true;
  }

  const calculateMaxId = order => {
    if(order?.items?.length > 0){
      const arr = order.items.map(item => +item?.id || 0);    
      return arr.sort((a, b) => b - a)[0];
    }     
    else return -1;
  }

  const findCity = id => {
    const city =  feathersStore.settings.citys?.find(c => +c.id === +id )    
    if(city) return feathersStore._translate(city.name, city.nameEnglish);
    else return "";   
  } 

  const findTransportation = (id) => {
  
    const city =  feathersStore.settings.citys?.find(c => +c.id === +id );  
   
    if(feathersStore?.tableId !== 'eshop')return 0;
    else return city?.transportation || 0;

  }

  //----> DERIVED TOTALS (pure - NEVER assign into formik.values). The five order-summary numbers
  // used to be computed by ASSIGNMENTS inside JSX value props (formik.values.grandTotal = ...).
  // That mutation wrote through an alias: formik 2.4.9's bare resetForm() sets state.values to
  // initialValues.current BY REFERENCE, so the render mutation poisoned initialValues.current,
  // making it permanently un-deep-equal to the inline initialValues prop. With
  // enableReinitialize on, the NEXT outer re-render - including the one caused by CLICKING
  // PROCEED TO PAYMENT (setTipModal) - then hard-reset the whole form behind the tip modal:
  // acceptedTerms flipped back to false (initialValues: isEshop ? false : true), guests lost
  // every typed field, and identityGap correctly refused the wiped order. sbroast: zero web
  // orders for 16 days, toast on every attempt; same wipe produced ragou's blank order before
  // the gate existed. Compute-and-merge instead: render reads _t.*, every commit path merges
  // computeTotals(values) into its snapshot.
  const computeTotals = (v) => {
    //free once the basket reaches the threshold - the rule every app shares (utils/deliveryFee.js)
    const _transportation = (((v.orderKind === "ClickAway") || freeDeliveryReached(cartSub, feathersStore.settings.transportation?.free)) ?
      0 : +findTransportation(v.hasOtherAddress ? +v.deliveryCity : +v.city)).toFixed(2);
    //CLAMPED, in precedence order, so grandTotal can never go below zero. It could: a loyalty
    //reward's value is fixed when it is EARNED and has no relation to the basket it is later spent
    //in, so a EUR10 reward against a EUR3 cart drove the total negative and the excess was destroyed
    //rather than carried. The ad-hoc coupon rail was already clamped where it is computed; loyalty
    //and the voucher were not. Each deduction is capped at what is left after the ones before it,
    //and these capped figures are what the commit paths record - so the discount written into
    //discountsArray always equals the money actually taken off the bill.
    const _gross = +(+cartSub + +_transportation).toFixed(2);
    const customerDiscount = Math.min(+discount || 0, Math.max(0, _gross));
    //Priced from the LIVE subtotal and re-checked against its minimum - never the figure frozen
    //in the cart when the code was typed. See voucherValueFor.
    const couponDiscount = Math.min(voucherValueFor(voucherDiscount, cartSub),
      Math.max(0, +(_gross - customerDiscount).toFixed(2)));
    const loyaltyDiscount = Math.min(+loyaltyReward || 0,
      Math.max(0, +(_gross - customerDiscount - couponDiscount).toFixed(2)));
    const grandTotal = (_gross - customerDiscount - couponDiscount - loyaltyDiscount).toFixed(2);
    return { transportation: _transportation, couponDiscount, loyaltyDiscount, customerDiscount, grandTotal };
  };
  computeTotalsRef.current = computeTotals;
  sendOrderRef.current = sendOrder;
  //<---- DERIVED TOTALS END

  const saveOrUpdateCompany = async(companyPhone, companyEmail, companyOrigin, typedAfm) => {
    try{
      const payload = {
        //optional-chained like its siblings below: a lookup that finds nothing returns {}, so
        //.afm is undefined and .trim() would throw and lose the whole company save. The HMRC
        //branch legitimately returns no afm field, so fall back to what the user typed rather
        //than persisting the company without its tax number. An ΑΦΜ AADE does not know comes back with a nil
        //afm - an object, which .trim() threw on too - so it goes through firstText.
        afm: firstText(aadeResponseRef.current?.afm, typedAfm),
        aadeResponse: aadeResponseRef.current,
        phone: companyPhone,
        email: companyEmail,
        origin: companyOrigin,
        addresses: addresses
      }
      if(companyIdRef.current){
        await feathersStore.updateCompany(companyIdRef.current, payload)
      }else{
        await feathersStore.createCompany(payload);
      }
    }catch(error){
      console.log(error);
    }
  }
  const parse_fix = price => {
    return price ? parseFloat(price).toFixed(2) : 0;
  }

  //---------------------> REWARD SYSTEM

  //Every OrderKind this order could still be committed as. In the cart the customer has not chosen
  //delivery vs takeaway yet, so the gate passes if ANY possible kind is allowed and the narrower check
  //happens here, where the choice is known.
  const possibleOrderKinds = () => {
    if(feathersStore.isTableOrder())return [OrderKind.webTable];
    const chosen = formRef.current?.values?.orderKind;
    if(chosen === 'ClickAway')return [OrderKind.webTakeAway];
    if(chosen === 'Delivery')return [OrderKind.webDelivery];
    return [OrderKind.webDelivery, OrderKind.webTakeAway];   //not chosen yet
  }


  const constructDiscount = (coupons, campaignsById = null) => {
    let _discount = 0;
    let discountsArray = [];
    const now = DateTime.now().setZone(feathersStore.timezone());
    const kinds = possibleOrderKinds();
    coupons.forEach(coupon => {
      const campaign = resolveCampaign(coupon, campaignsById);
      if(!campaign)return;                                   //campaign deleted: stop honouring the orphan
      if(coupon.deleted)return;                              //server delete tombstone
      if(campaign.general)return;                            //typed-code vouchers go through applyVoucher
      //Coerced: the touch-POS keyboard writes completion as a string where the desktop writes a number.
      if(+campaign.completion > 0)return;                   //completion > 0 is a loyalty target
      if(!(DateTime.fromISO(coupon.stop) > now))return;
      if(coupon.start && !(DateTime.fromISO(coupon.start) < now))return;
      if(campaign.minimumOrder && +cartSub < +campaign.minimumOrder)return;   //never enforced here before
      if(!campaignTargetsCustomerClient(campaign, kinds))return;
      //A first-order campaign is offered only to a signed-in customer with no prior orders. The row
      //is labelled so the server can recognise and re-validate it; everything else stays 'AdHoc'.
      if(campaign.firstOrderOnly && firstOrderEligible !== true)return;
      //Clamp against what is left of the cart: unclamped Cash coupons stacked straight past zero and
      //grandTotal has no Math.max anywhere on the path.
      const remaining = +(+cartSub - _discount).toFixed(2);
      if(remaining <= 0)return;
      let disc = campaign.type === 'Percent' ? +parse_fix(cartSub * +campaign.reward / 100)
        : campaign.type === 'Cash' ? +campaign.reward
        : 0;                                                  //Bonus pays a gift line, never money
      disc = Math.min(disc, remaining);
      if(!(disc > 0))return;
      _discount = +(_discount + disc).toFixed(2);
      discountsArray.push({
        type: campaign.type,
        label: campaign.firstOrderOnly ? 'FirstOrder' : 'AdHoc',
        couponId: campaign._id,   //the server re-reads the campaign by this id
        value: disc,   //NUMBER: string values concatenate in the admin's applied-discount rollup
        reward: campaign.reward,
        applied: false,
        display: campaign.display,
        promotionText: campaign.promotionText
      })
    })   
    setDiscount(_discount);
    discountsArrayRef.current = discountsArray;
  }

  //---------------------------------->

  // Returns the settings day-object (openingHours, closingHours, hoursEnabled)
  // for a given Luxon DateTime, using the same index formula as header.js.
  const getDaySchedule = (luxonDate) => {
    if (!luxonDate) return null;
    const dayIndex = (luxonDate.weekday + 6) % 7;
    const dayName = Info.weekdays('long', { locale: 'en-US' })[dayIndex].toLowerCase();
    return feathersStore?.settings?.[dayName] ?? null;
  };

  // Returns { minTime, maxTime } as Luxon DateTimes for the TimePicker,
  // or nulls when the restaurant is open all day.
  const getTimeConstraints = (luxonDate) => {
    const dayData = getDaySchedule(luxonDate);
    if (!dayData || dayData.hoursEnabled) return { minTime: null, maxTime: null };
    const isAllDay = dayData.openingHours === '00:00' && dayData.closingHours === '23:59';
    if (isAllDay) return { minTime: null, maxTime: null };
    const base = luxonDate.setZone(feathersStore.timezone(), { keepLocalTime: true });
    return {
      minTime: base.set({ hour: +dayData.openingHours.split(':')[0], minute: +dayData.openingHours.split(':')[1], second: 0, millisecond: 0 }),
      maxTime: base.set({ hour: +dayData.closingHours.split(':')[0], minute: +dayData.closingHours.split(':')[1], second: 0, millisecond: 0 }),
    };
  };

  // The invoice ("Issue Invoice") sub-form is Greek-fiscal SHAPED: it demands a ΔΟΥ (tax office),
  // which no non-Greek registry returns, so a UK company could never satisfy the schema. The
  // server side is already region-aware (soap.class.js routes uk/gb to HMRC and returns the same
  // field shape), so only the client needed branching. Greek-fiscal tenants keep TODAY'S schema,
  // labels, validator and behaviour byte-for-byte; everything conditional below is the non-Greek
  // leg. isGreekFiscal() defaults to Greek when settings are unset, so legacy tenants are unmoved.
  const isGreekInvoice = feathersStore.isGreekFiscal();

  const validationSchema = Yup.object({ //Three ways to do the validation

    firstName: Yup.string()
      .when("isEshop", {
      is: true,
      then:  Yup.string()
      .max(255, checkout.less255)
      .required(checkout.required),
      otherwise: Yup.string().nullable().notRequired()
    }),    
    lastName: Yup.string()
      .when("isEshop", {
      is: true,
      then:  Yup.string()
      .max(255, checkout.less255)
      .required(checkout.required),
      otherwise: Yup.string().nullable().notRequired()
      })
    ,
    /* ADDRESS FIELDS ARE KEYED ON orderKind, NOT ON isEshop ALONE. A ClickAway order is collected
       at the counter, so there is no address to deliver to - requiring one made a collection order
       unsubmittable, which is what a marketing QR (a cup, a flyer: no __table, so eshop mode) lands
       a customer in. The mobile apps have always worked this way: AddressData.js:410 renders the
       address block only `{feathersStore.isDelivery ? ... : pickupNoAddress}`, so on pickup the
       fields do not exist and their errors are never set. This brings the web to the same rule. */
    street: Yup.string()
      .when(["isEshop", "reducedCheckoutData", "orderKind"], {
      is: (isEshop, reducedCheckoutData, orderKind) => isEshop && !reducedCheckoutData && orderKind === "Delivery",
      then:  Yup.string()
      .max(50, checkout.less50)
      .required(checkout.required),
      otherwise: Yup.string().nullable().notRequired()
      })
    ,
    streetNumber: Yup.string()
      .when(["isEshop", "reducedCheckoutData", "orderKind"], {
      is: (isEshop, reducedCheckoutData, orderKind) => isEshop && !reducedCheckoutData && orderKind === "Delivery",
      then:  Yup.string()
      .max(10, checkout.less10)
      .required(checkout.required),
      otherwise: Yup.string().nullable().notRequired()
      }),
    city: Yup.number()
      .when(["isEshop", "reducedCheckoutData", "orderKind"], {
        is: (isEshop, reducedCheckoutData, orderKind) => isEshop && !reducedCheckoutData && orderKind === "Delivery",
        then: (schema) =>
          schema.moreThan(0, checkout.required).required(checkout.required)
            //a saved address can hold a city the shop has since switched off: the list no longer offers it, but the
            //value stayed and passed - refused, so the customer picks a city the shop delivers to
            .test('served-city', checkout.required, (id) => isServedCity(feathersStore.settings?.citys, id)),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    postcode: Yup.string()
     .when(["isEshop", "reducedCheckoutData"], {
      is: (isEshop, reducedCheckoutData) => isEshop && !reducedCheckoutData,
      then:  Yup.string()
      .max(10, checkout.less10),
    //  .required(checkout.required),
      otherwise: Yup.string().nullable().notRequired()
    })
    ,
    email: Yup.string()
      .when(["isEshop", "reducedCheckoutData"], {
      is: (isEshop, reducedCheckoutData) => isEshop && !reducedCheckoutData,
      then:  Yup.string()
      .email(checkout.invalidEmail)
      .required(checkout.required)
      })
    ,
    phone: Yup.string()
      .when(["isEshop", "reducedCheckoutData"], {
      is: (isEshop, reducedCheckoutData) => isEshop && !reducedCheckoutData,
      then:  Yup.string()
      // Yup's .matches() passes an ABSENT value through and only tests a present one; the .test()
      // below reproduces that exactly, so optional phone fields keep behaving as they do today.
      .test('phone', checkout.invalidPhone, v => v === undefined || v === null || feathersStore.phoneIsValid(v))
      .required(checkout.required),
      otherwise: Yup.string().nullable().notRequired()
      })
    ,     
    transportation: Yup.number(), 
    customerDiscount: Yup.number(), 
    couponDiscount: Yup.number(),
    loyaltyDiscount: Yup.number(),
    grandTotal: Yup.number(),
    hasOtherAddress:  Yup.boolean()
      .required(checkout.required),
    isCompany:  Yup.boolean()
      .required(checkout.required),
    comments: Yup.string()
      .max(500, checkout.textareaMax),
    paymentMethod:  Yup.string() 
      .required(checkout.required)
      .oneOf(["Cash", "BankTransfer", "NBG", "PayPal"], checkout.invalidPayment),
    orderKind:  Yup.string() 
      .required(checkout.required)
      .oneOf(["ClickAway", "Delivery"], checkout.required),
     acceptedTerms: Yup.boolean()
      .when("isEshop", {
        is: true,
        then: Yup.boolean().required(checkout.required).oneOf([true], checkout.terms),
        otherwise: Yup.boolean().notRequired()
      }),
    allowRecurring: Yup.boolean(),
    subscriptionInterval: Yup.number(),
    deferredOrder: Yup.boolean(),
    deferredDate: Yup.mixed().when('deferredOrder', {
      is: true,
      then: Yup.mixed().required(checkout.required).nullable(false)
    }),
    deferredTime: Yup.mixed().when('deferredOrder', {
      is: true,
      then: Yup.mixed().required(checkout.required).nullable(false)
    }),
    
      //---- Company sub - form validation
    afm:  Yup.string().when('isCompany', {
      is: true,
      then: isGreekInvoice ?
        Yup.string()
          .max(20, checkout.less20)
          .required(checkout.required)
        :
        //Non-Greek: the number is a VAT registration number, not a 9-digit ΑΦΜ. Accept a UK VRN
        //shape (spaces stripped, optional case-insensitive GB, then 9 or 12 digits).
        Yup.string()
          .max(20, checkout.less20)
          .test('vrn', checkout.invalidVat, value => !value || Utils.validateVrn(value))
          .required(checkout.required)
    }),
    doyDescription:  Yup.string().when('isCompany', {
      is: true,
      //ΔΟΥ is a Greek tax-office field. HMRC returns none, so requiring it made the non-Greek
      //invoice form unsatisfiable; outside Greece the field is not rendered and not required.
      then: isGreekInvoice ?
        Yup.string()
          .max(50, checkout.less50)
          .required(checkout.required)
        :
        Yup.string()
          .max(50, checkout.less50)
    }),
    legalName:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(255, checkout.less255)
        .required(checkout.required)
    }),
    legalDescription:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(255, checkout.less255)
        .required(checkout.required)
    }),
    firmActDescription:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(255, checkout.less255)
        .required(checkout.required)
    }),
    postalAddress:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(50, checkout.less50)
        .required(checkout.required)
    }),
    postalAddressNo:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(10, checkout.less10)
        .required(checkout.required)
    }),
    postalAreaDescription:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(50, checkout.less50)
        .required(checkout.required)
    }),
    postalZipCode:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .max(10, checkout.less10)
        .required(checkout.required)
    }),
    companyPhone:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .test('phone', checkout.invalidPhone, v => v === undefined || v === null || feathersStore.phoneIsValid(v))
        .required(checkout.required)
    }),
    companyEmail:  Yup.string().when('isCompany', {
      is: true,
      then:  Yup.string()
        .email(checkout.invalidEmail)
        .required(checkout.required)
    }),
    companyOrigin: Yup.number().when('isCompany', {
      is: true,
      then:  Yup.number()
      .required(checkout.required)
    }),

      //----- Other - Address - Sub - form validation ----------
    deliveryFirstName:   Yup.string().when('hasOtherAddress', {
      is: true,
      then:  Yup.string()
        .max(255, checkout.less255)
        .required(checkout.required)
    }), 
    deliveryLastName:   Yup.string().when('hasOtherAddress', {
      is: true,
      then:  Yup.string()
        .max(255, checkout.less255)
        .required(checkout.required)
    }),          
    deliveryStreet:Yup.string().when('hasOtherAddress', {
      is: true,
      then:  Yup.string()
        .max(50, checkout.less50)
        .required(checkout.required)
    }),
    deliveryStreetNumber:Yup.string().when('hasOtherAddress', {
      is: true,
      then:  Yup.string()
        .max(10, checkout.less10)
        .required(checkout.required)
    }),
    deliveryCity: Yup.number().when('hasOtherAddress', {
      is: true,
      then:  Yup.number()         
       .moreThan(0, checkout.required)
       .test('served-city', checkout.required, (id) => isServedCity(feathersStore.settings?.citys, id))
    }),        
    deliveryOrigin: Yup.number().when('hasOtherAddress', {
      is: true,
      then:  Yup.number()         
        .required(checkout.required)
    }),
    deliveryPostcode: Yup.string().when('hasOtherAddress', {
      is: true,
      then:  Yup.string()
        .max(10, checkout.less10)
     //   .required(checkout.required)
    }),
    deliveryPhone:Yup.string().when('hasOtherAddress', {
      is: true,
      then:  Yup.string()
        .test('phone', checkout.invalidPhone, v => v === undefined || v === null || feathersStore.phoneIsValid(v))
        .required(checkout.required)
    })  
        
  })

  // These render the COMPUTED number passed via the Field's `value` prop (Formik forwards extra
  // props to custom components). They used to read `field.value` — formik state — which worked
  // only because the JSX assigned into formik.values during render; with that mutation removed
  // (see computeTotals) formik state legitimately stays at its initial 0, so field.value would
  // display TOTAL 0€ while the money paths charge the real amount. Render the prop, keep the
  // field.value fallback for safety.
  const grandTotalComponent = ({field, form, value}) => {
    return(
    <span>{feathersStore.formatMoney(value ?? field.value)}</span>
    )
  }

  const transportationComponent = ({field, form, value}) => {
    return(
    <span>{feathersStore.formatMoney(value ?? field.value)}</span>
    )
  }

  const discountComponent = ({field, form, value}) => {
    return(
    <span>{feathersStore.formatMoney(value ?? field.value)}</span>
    )
  }

  
  //----------------- Invoice methods --------------

  const search = async(afm) => { 
    let company;
    const companyFromDb = await feathersStore.findCompany(afm);
    if(companyFromDb?.data?.length > 0)company = companyFromDb?.data[0];
    //A lookup belongs to this ΑΦΜ alone. The record id was only ever set, never cleared, and the AADE answer
    //outlived a failed call: after a found ΑΦΜ A, a lookup of B (not on file) made the save patch record A with
    //B's afm, phone, email and addresses (patchCompanyAddresses too). The ref goes back to undefined, not null:
    //the save sends it as aadeResponse, and a patch with null would erase the record's stored answer.
    const previousId = companyIdRef.current;
    companyIdRef.current = company?._id;
    aadeResponseRef.current = undefined;
    if(previousId && previousId !== company?._id) await clearAdditionalForm();
    aadeResponseRef.current = await searchAade(afm, company);
    if(company?._id){
      companyIdRef.current = company._id
      await fillAdditionalForm(company);
    }else{
      let data ={
        street: aadeText(aadeResponseRef.current?.postal_address),
        streetNumber: aadeText(aadeResponseRef.current?.postal_address_no),
        postcode: aadeText(aadeResponseRef.current?.postal_zip_code),
        city: aadeText(aadeResponseRef.current?.postal_area_description),
        active:  true
      }
      //A lookup that came back empty (HMRC returns {} on any error) must not replace an address
      //the customer already entered with a blank row. Greek path unchanged.
      if(isGreekInvoice || Object.values(data).some(v => v && v !== true)){
        setAddresses([data]);
      }
    }

  }

  const searchAade = async (afm, stored) => {
    const aadeResponse = await feathersStore.createSoap(afm);
    await fillBasicForm(aadeResponse, stored);
    return aadeResponse;
  }

  /* ---- Delivery-address geocoding -------------------------------------
     The customer may always type freely; picking a suggestion additionally
     captures exact coordinates, which ride to the order (and on to the
     courier / DeliverasNow) instead of the historic 0,0. */
  const clearOrderCoordinates = () => {
    formRef.current?.setFieldValue('latitude', 0);
    formRef.current?.setFieldValue('longitude', 0);
  };

  // Free typing invalidates a previously picked pin.
  const onStreetInput = (text) => {
    formRef.current?.setFieldValue('street', text);
    clearOrderCoordinates();
  };

  // A hand-edited number is a different building than the one that was picked.
  const onStreetNumberInput = () => clearOrderCoordinates();

  /* "Use my location" for GUESTS only. A logged-in customer picks from the
     saved addresses shown above this form; a guest has none, so this is their
     one-tap alternative to typing. Mirrors the RN app, which likewise offers
     find-my-location at checkout only when the user is the default account
     (AddressData.js) and hides it entirely without a tenant maps key. */
  const canUseMyLocation = feathersStore.canGeocode()
    && feathersStore.user?.email === DEFAULT_EMAIL;

  const useMyLocation = () => {
    if(!canUseMyLocation || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (position) => {
      feathersStore.setHasLocationPermission(true);
      const { latitude, longitude } = position.coords;
      try{
        const maps = await loadGoogleMaps(feathersStore.tenantMapsKey());
        if(!maps) return;
        const geocoder = new maps.Geocoder();
        const { results } = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
        const parsed = results?.[0] ? extractAddress(results[0].address_components) : {};
        const matched = matchTenantCity(
          parsed.cityCandidates, feathersStore.settings?.citys,
          (c) => feathersStore._translate(c.name, c.nameEnglish));
        // Reuse the same writer the autocomplete uses, so both paths fill the
        // form and the coordinates identically.
        onAddressResolved({
          street: parsed.street || '',
          streetNumber: parsed.streetNumber || '',
          postcode: parsed.postcode || '',
          cityId: matched ? matched.id : null,
          latitude,
          longitude
        });
      }catch(error){ console.log('reverse geocoding failed', error); }
    }, (error) => {
      feathersStore.setHasLocationPermission(false);
      console.log('geolocation unavailable', error?.message);
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
  };

  const onAddressResolved = (resolved) => {
    formRef.current?.setFieldValue('street', resolved.street || '');
    if(resolved.streetNumber) formRef.current?.setFieldValue('streetNumber', resolved.streetNumber);
    if(resolved.postcode) formRef.current?.setFieldValue('postcode', resolved.postcode);
    // Only prefill the city when it maps to one of the tenant's OWN enabled
    // cities — delivery pricing and the submit guard are city-id based.
    if(resolved.cityId) formRef.current?.setFieldValue('city', resolved.cityId);
    formRef.current?.setFieldValue('latitude', resolved.latitude);
    formRef.current?.setFieldValue('longitude', resolved.longitude);
  };
  
  // Only a stored value replaces what the form holds (see utils/storedCompanyContact.js): a record without one must
  // not wipe what the customer typed or the reopened order carried.
  const fillAdditionalForm = async(company) => {
    const { phone, email } = storedCompanyContact(company);
    if(phone) await formRef.current.setFieldValue('companyPhone', phone);
    if(email) await formRef.current.setFieldValue('companyEmail', email);
    filledFromRef.current = (phone || email) ? company?._id : undefined;
    // a companies record keeps it as `origin` (what saveOrUpdateCompany writes, and the admin apps too):
    // reading `companyOrigin` always gave 1, and the next save filed 1 over the origin the admin had chosen
    await formRef.current.setFieldValue('companyOrigin', +company?.origin || 1);
    setAddresses(company?.addresses);
  }

  //What a previous lookup's record filled in, which the next ΑΦΜ must not inherit (the form's own defaults). Phone
  //and email only when that record filled them (filledFromRef): otherwise they are the customer's own.
  const clearAdditionalForm = async() => {
    if(filledFromRef.current){
      await formRef.current.setFieldValue('companyPhone', '');
      await formRef.current.setFieldValue('companyEmail', '');
      filledFromRef.current = undefined;
    }
    await formRef.current.setFieldValue('companyOrigin', 1);
    setAddresses([]);
  } 
  
  // A registry lookup that returns nothing must not wipe what the customer already typed. The
  // Greek AADE payload fills every field, so Greek tenants keep the unconditional assignment
  // byte-for-byte; HMRC's success payload legitimately omits ΔΟΥ / street-number / legal status
  // and its error path returns {}, so outside Greece only non-empty values are written.
  // Every value goes through aadeText: an AADE field left empty arrives as an object ({ 'xsi:nil': ['true'] }),
  // and toString() on it wrote "[object Object]" into the form (see utils/aadeText.js).
  const setLookupField = async (name, value) => {
    const trimmed = aadeText(value);
    if(isGreekInvoice || trimmed) await formRef.current.setFieldValue(name, trimmed);
  }

  // AADE first, then the stored company (firstText). A nil firm_act_tab made `.item.find` throw and lose the
  // whole lookup. A sole proprietor has no legal_status_descr and the field is required, so after the stored
  // value it falls back to AADE's own firm_flag_descr (ΕΠΙΤΗΔΕΥΜΑΤΙΑΣ - HMRC sends '', which writes nothing).
  const fillBasicForm = async(aade, stored) => {
    if(aade){
      const activity = firstText(aadeMainActivity(aade), stored?.firmActDescription);
      activity && await setLookupField('firmActDescription', activity);
      await setLookupField('legalName', firstText(aade.onomasia, stored?.legalName));
      await setLookupField('legalDescription', firstText(aade.legal_status_descr, stored?.legalDescription, aade.firm_flag_descr));
      await setLookupField('doyDescription', firstText(aade.doy_descr, stored?.doyDescription));
      await setLookupField('postalAddress', firstText(aade.postal_address, stored?.postalAddress));
      await setLookupField('postalAddressNo', firstText(aade.postal_address_no, stored?.postalAddressNo));
      await setLookupField('postalAreaDescription', firstText(aade.postal_area_description, stored?.postalAreaDescription));
      await setLookupField('postalZipCode', firstText(aade.postal_zip_code, stored?.postalZipCode));

    }
  }

  const renderAddressItem = ( item, index ) => (
   
    <AddressComponent
      companyId={companyIdRef.current}
      setAddresses={setAddresses}
      key={index}
      onPress={setDeliveryAddress(item)}
      street={item.street}
      city={item.city}
      postcode={item?.postcode}
      streetNumber={item.streetNumber}
      active={item.active} 
      item={item}   
      itemIndex={index} 
      disableDelete={addresses?.length < 2}
    />
    );

  
  //----------------- LoadingOrderModal controls -----------------START

  // Absolute, not toggles. These are handed to LoadingOrderModal and fired later, so `isLoading`
  // could be read from a stale closure - and a toggle that lands on TRUE re-opens the modal, which
  // (the modal commits on becoming visible) re-sends the order.
  const whenError = () => {
    setLoading(false);
  }

  const CompleteOrder = ()=> {
    setLoading(false);
    setComplete(true);
  }

  const setOrder = ()=> {
    setComplete(false);
    navigate("/") ;
  }

   //----------------- LoadingOrderModal controls -----------------END

  const goToTerms = () => {
    navigate("/termsnconditions") ;     
  }   

  const calculateDefaultPaymentMethod = () => {
    if((feathersStore?.tableId === 'eshop' && !feathersStore.settings?.disableCashPayments) ||
      (feathersStore?.tableId !== 'eshop' && !feathersStore.settings?.disableOandPCashPayments))return 'Cash';
    else if((feathersStore?.tableId === 'eshop' && feathersStore.settings?.hasCardPayments) ||
      (feathersStore?.tableId !== 'eshop' && feathersStore.settings?.hasOandPCardPayments))return 'PayPal';
    else return 'BankTransfer';
  }
  
  const renderCartItems = [
    ...cartItems.map( (item, index) => (
    <CheckoutComponent
      key={index}
      sku={item.productSKU}
      title={constructTitle(item)}
      data={constructData(item)}
      quantity={item.product_quantity}
      total={item?.product_totalPrice || 0}
    />
  )),
    ...bonusGifts.map((g, i) => (
      <CheckoutComponent
        key={`gift-${i}`}
        sku=""
        title={`${common.freeGift || "Free gift"}: ${g.name}`}
        data=""
        quantity={g.qty}
        total={0}
      />
    ))
  ]
  
  return(   
    <>    
      {formRef.current?.values && 
        <LoadingOrderModal
          visible={isLoading}
          whenSent={CompleteOrder}
          whenError={whenError}
          total={+computeTotals(formRef.current?.values || {}).grandTotal}
        />
      }
      <OrderCompleteModal
        visible={isComplete}
        onClickFinish={setOrder}        
      /> 
      <div className="header-section">      
        <div className="grey-header">{common.checkout}</div>    
      </div>
      <div className="info-wrapper">
        <div className="info-text">
          <div>
            <InfoOutlinedIcon className="info-icon"/>
          </div>
          <div>
            {checkout.alergies}
          </div>
        </div>
        <div className="info-line"></div>        
      </div>
     
      { feathersStore.user?.email === DEFAULT_EMAIL &&  feathersStore.tableId === 'eshop' &&
         <div className="checkout-login">
          <div>{checkout.doYouHave}&nbsp; </div>
          <Registration title = {checkout.here} />
          <div>&nbsp;{checkout.toConnect}</div>
       </div>}
      <>
      <Formik 
        innerRef={formRef}  
        initialValues={{
        
          firstName: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL ? feathersStore.user?.firstname : 
            feathersStore?.orderItem?.firstName || "",          
          lastName: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL ? feathersStore.user?.lastname : 
            feathersStore?.orderItem?.lastName || "",
          street: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ? 
            feathersStore.user?.addresses.find(addr => addr.active)?.street : feathersStore?.orderItem?.street || "",
          streetNumber: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ? 
            feathersStore.user?.addresses.find(addr => addr.active)?.streetNumber : feathersStore?.orderItem?.streetNumber || "",
          city: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ? 
            feathersStore.user?.addresses.find(addr => addr.active)?.city : feathersStore?.orderItem?.city || -1, 
          origin: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ? 
            feathersStore.user?.addresses.find(addr => addr.active)?.origin : feathersStore?.orderItem?.origin || 1, 
          postcode: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ?
            feathersStore.user?.addresses.find(addr => addr.active)?.postcode : feathersStore?.orderItem?.postcode || "",
          // Coordinates ride along with the chosen saved address (they were captured
          // when it was created), so a returning customer keeps an exact pin without
          // paying for another lookup.
          latitude: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ?
            (feathersStore.user?.addresses.find(addr => addr.active)?.latitude || 0) : feathersStore?.orderItem?.latitude || 0,
          longitude: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL && feathersStore.user?.addresses?.length > 0 ?
            (feathersStore.user?.addresses.find(addr => addr.active)?.longitude || 0) : feathersStore?.orderItem?.longitude || 0,
          phone: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL ? feathersStore.user?.phone : 
            feathersStore?.orderItem?.phone || "",
          email: feathersStore.isAuthenticated && feathersStore.user?.email !== DEFAULT_EMAIL ? feathersStore.user?.email : 
            feathersStore?.orderItem?.email || "",

          
          isEshop, //Helper for Yup validation
          reducedCheckoutData: feathersStore.settings?.reducedCheckoutData || false, //Helper for Yup validation
          isCompany: feathersStore?.orderItem?.isCompany || false,
          afm: feathersStore?.orderItem?.afm || "",
          doyDescription: feathersStore?.orderItem?.doyDescription || "",
          legalName: feathersStore?.orderItem?.legalName || "",
          legalDescription: feathersStore?.orderItem?.legalDescription || "",
          firmActDescription: feathersStore?.orderItem?.firmActDescription || "",
          postalAddress: feathersStore?.orderItem?.postalAddress || "",
          postalAddressNo: feathersStore?.orderItem?.postalAddressNo || "",
          postalAreaDescription: feathersStore?.orderItem?.postalAreaDescription || "",
          postalZipCode: feathersStore?.orderItem?.postalZipCode || "",
          companyPhone: feathersStore?.orderItem?.companyPhone || "",
          companyEmail: feathersStore?.orderItem?.companyEmail || "",
          companyOrigin: +feathersStore?.orderItem?.companyOrigin || 1, 
          hasOtherAddress: feathersStore?.orderItem?.hasOtherAddress || false,
          deliveryFirstName: feathersStore?.orderItem?.deliveryFirstName || '',
          deliveryLastName: feathersStore?.orderItem?.deliveryLastName || '',
          deliveryStreet: feathersStore?.orderItem?.deliveryStreet || '',
          deliveryStreetNumber: feathersStore?.orderItem?.deliveryStreetNumber || '',
          deliveryCity: +feathersStore?.orderItem?.deliveryCity || -1, 
          deliveryOrigin: +feathersStore?.orderItem?.deliveryOrigin || 1, 
          deliveryPostcode: feathersStore?.orderItem?.deliveryPostcode || '',       
          deliveryPhone: feathersStore?.orderItem?.deliveryPhone || '',
          comments: feathersStore?.orderItem?.comments || '',
          deferredOrder: false,
          deferredDate: null,
          deferredTime: null,
          paymentMethod: calculateDefaultPaymentMethod(),
          orderKind: feathersStore?.settings?.disableDelivery ? "ClickAway" : "Delivery",
          acceptedTerms: isEshop ? false : true,
          grandTotal:0,  
          transportation: 0,
          customerDiscount: 0,
          couponDiscount: 0,
          loyaltyDiscount: 0,
          allowRecurring: false,  //recurring Viva payments
          subscriptionInterval: 1    //interval of next payment in MONTHS.  
        }}
      
      enableReinitialize={vivaSmart}
      validationSchema= {validationSchema}    
      
      onSubmit={(values, { setSubmitting }) => {  
        sendOrder(values);
        setSubmitting(false);
        ;
      }}
    >
    {formik => {
      const _t = computeTotals(formik.values); // derived totals - render reads, never writes
      // The address block is DELIVERY-ONLY, matching the Yup rules and identityGap above, and the
      // mobile apps (AddressData.js:410 renders it only `{feathersStore.isDelivery ? ... }`).
      // A ClickAway order is collected at the counter, so showing starred "required" address fields
      // that nothing validates just invites the customer to fill in an address nobody will use.
      const _showAddress = !feathersStore.settings?.reducedCheckoutData
        && formik.values.orderKind === "Delivery";
      return(
    <Form onChange={(e) => {
      if ((e.target.name === 'paymentMethod' && !vivaSmart)) setFormState(s => ({ ...s, paymentMethod: e.target.value }));
    }}>
      <div className="column-flex-center main-container">
        <div className="row-flex-start-start-wrap form-container" >
          <div className="left-container">   
          {/* Guest detection is by EMAIL, not firstname: every visitor shares the one defaultUser
              account, so if a guest edits "their" profile (meinKonto) the shared doc's firstname stops
              being 'default' and the old firstname check flipped this O&P gate for EVERYONE, showing the
              eshop personal-data form at the table (coast 2026-07-18). email is the immutable identity. */}
          {(feathersStore.tableId === 'eshop' || (feathersStore.tableId !== 'eshop' && feathersStore.user?.email !== DEFAULT_EMAIL)) &&
              <>
            <div className="checkout-order-table-title">{checkout.checkoutData}</div> 
                   
            <div className=" row-container" >
              <div className="two-quarters-container">                
                <label htmlFor="firstName" style={{fontWeight: 'bold'}}>{common.firstName}</label><span style={{color: 'red'}}>*</span>                
                <Field name="firstName" type="text" className="form-control" />
                <ErrorMessage name="firstName" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>
              <div className="two-quarters-container-right">                
                <label htmlFor="lastName" style={{fontWeight: 'bold'}}>{common.lastName}</label><span style={{color: 'red'}}>*</span>                
                <Field name="lastName" type="text" className="form-control" />
                <ErrorMessage name="lastName" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>                
            </div>
            {/* Collection: no address is taken, so say so instead of leaving a silent gap where
                five starred fields used to be. */}
            {!feathersStore.settings?.reducedCheckoutData && formik.values.orderKind === "ClickAway" &&
            <div className="row-container">
              <div className="pickup-no-address">{checkout.pickupNoAddress}</div>
            </div>}
            {_showAddress &&
            <div className="row-container">
              <div className="three-quarters-container">
                <label style={{fontWeight: 'bold'}} htmlFor="street">{common.street}</label><span style={{color: 'red'}}>*</span>
                {canUseMyLocation &&
                  <button type="button" className="use-my-location-btn" onClick={useMyLocation}>
                    <MyLocationIcon style={{fontSize: 16}}/>
                    <span>{account.myLocation}</span>
                  </button>
                }
                {/* Places-backed street field. `plain` keeps the .form-control look,
                    and with no tenant key it IS just a .form-control input. */}
                <AddressAutocomplete
                  plain
                  inputClassName="form-control"
                  value={formik.values.street || ""}
                  onInputChange={onStreetInput}
                  onResolved={onAddressResolved}
                />
                <ErrorMessage name="street" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>
              <div className="one-quarter-container-right">
                <label style={{fontWeight: 'bold'}} htmlFor="streetNumber">{common.streetNumber}</label><span style={{color: 'red'}}>*</span>
                <Field name="streetNumber" type="text" className="form-control" onInput={onStreetNumberInput} />
                <ErrorMessage name="streetNumber" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>
            </div>}
            {_showAddress &&
            <div className="row-container" >
              <div className="two-quarters-container">
                <label style={{fontWeight: 'bold'}} htmlFor="city">{common.city}</label><span style={{color: 'red'}}>*</span>
                <Field name="city" as="select" className="form-control" >
                  <option className="option-item" value={-1}>{common.selectSmall}</option>
                {feathersStore?.settings?.citys?.filter(c => c.enabled)?.map((i, index)=> (
                    <option key={index} className="option-item" value={+i.id}>{feathersStore._translate(i.name , i.nameEnglish)}</option>
                  ))}
                </Field>
                <ErrorMessage name="city" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>
            </div>}
            {_showAddress &&
            <div className="row-container" >
              <div className="three-quarters-container">
                <label htmlFor="origin" style={{fontWeight: 'bold'}}>{common.origin}</label><span style={{color: 'red'}}>*</span>
                <Field name="origin" as="select" className="form-control">
                  {origins?.map((i, index)=> (
                    <option key={index} className="option-item" value={+i.id}>{feathersStore.translate('en', i.label , i.labelEnglish)}</option>
                  ))}
                </Field>
                <ErrorMessage name="origin" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>
              <div className="one-quarter-container-right">
                <label htmlFor="postcode" style={{fontWeight: 'bold'}}>{common.postcode}</label>
                <Field name="postcode" type="text" className="form-control" />
                <ErrorMessage name="postcode" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
              </div>
            </div>}
            {/* Pin calibration for the delivery point. Self-gating: renders
                nothing unless the tenant has IS_GEO + its own maps key. */}
            {_showAddress &&
            <div className="row-container">
              <div className="three-quarters-container">
                <AddressPinPicker
                  compact
                  latitude={formik.values.latitude}
                  longitude={formik.values.longitude}
                  onConfirm={onAddressResolved}
                />
              </div>
            </div>}
              {!feathersStore.settings?.reducedCheckoutData &&
              <div className="row-container">
                <div className="two-quarters-container">
                  <label htmlFor="phone" style={{fontWeight: 'bold'}}>{common.phone}</label><span style={{color: 'red'}}>*</span>
                  <Field name="phone" type="text" className="form-control" />
                  <ErrorMessage name="phone" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                </div>
              </div>}
              {!feathersStore.settings?.reducedCheckoutData &&
              <div className="row-container">
                <div className="two-quarters-container">
                  <label style={{fontWeight: 'bold'}} htmlFor="email">{common.email}</label><span style={{color: 'red'}}>*</span>
                  <Field name="email" type="email" className="form-control" />
                  <ErrorMessage name="email" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                  <small id="emailHelp" className="form-text text-muted">{common.emailMessage}</small>
                </div>
              </div>}   
              </>
              }    
              {feathersStore.tableId === 'eshop' &&
                <div className="checkbox-container">
                  <Field type="checkbox"   name="isCompany" 
                      style={{width: 16, height: 16}}/>
                  <label className="form-check-label" htmlFor="isCompany" >
                    {checkout.isCompany}
                  </label>
                </div>
              }
              <SlideDown className='my-dropdown-slidedown'>
                {formik.values.isCompany && 
                <div style={{marginTop: 20}}>
                  <div className="row-container">
                    <div className="two-quarters-container">                      
                      <label htmlFor="afm" style={{fontWeight: 'bold'}}>{checkout.companyAfm}</label><span style={{color: 'red'}}>*</span>                      
                      <div className="row-container">
                        <div>
                          <Field type="text" className="form-control" name="afm" />
                          <ErrorMessage name="afm" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                        </div>
                        <button     
                          type="button"                     
                          className="afm-search-button"         
                          onClick={() => search(formik.values.afm, formik)}
                          >
                            <span>{<SearchIcon />}</span>
                        </button>                     
                      </div>      
                    </div>
                    <div className="two-quarters-container-right">                     
                      <label htmlFor="legalName" style={{fontWeight: 'bold'}}>{checkout.legalName}</label><span style={{color: 'red'}}>*</span>                    
                      <Field name="legalName" type="text" className="form-control" />
                      <ErrorMessage name="legalName" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>                
                  </div>
                  {/* ΔΟΥ (tax office) is Greek-only — no non-Greek registry has one, so outside
                      Greece the field is dropped and the company type takes the left slot. */}
                  {isGreekInvoice ?
                  <div className="row-container">
                    <div className="two-quarters-container">
                      <label htmlFor="doyDescription" style={{fontWeight: 'bold'}}>{checkout.doyDescription}</label><span style={{color: 'red'}}>*</span>
                      <Field type="text" className="form-control" name="doyDescription" />
                      <ErrorMessage name="doyDescription" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                    <div className="two-quarters-container-right">
                      <label htmlFor="legalDescription" style={{fontWeight: 'bold'}}>{checkout.legalDescription}</label><span style={{color: 'red'}}>*</span>
                      <Field name="legalDescription" type="text" className="form-control" />
                      <ErrorMessage name="legalDescription" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                  </div>
                  :
                  <div className="row-container">
                    <div className="two-quarters-container">
                      <label htmlFor="legalDescription" style={{fontWeight: 'bold'}}>{checkout.legalDescription}</label><span style={{color: 'red'}}>*</span>
                      <Field name="legalDescription" type="text" className="form-control" />
                      <ErrorMessage name="legalDescription" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                  </div>
                  }
                  <div className="row-container">
                    <div className="two-quarters-container">
                      <label className="company-label" htmlFor="firmActDescription" style={{fontWeight: 'bold', alignSelf: 'baseline'}}>{checkout.firmActDescription}<span style={{color: 'red'}}>*</span> </label>                   
                      <Field name="firmActDescription" type="text" className="form-control" />
                      <ErrorMessage name="firmActDescription" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>                    
                  </div>                  
                  <div className="row-container" >
                    <div className="two-quarters-container">
                      <label className="company-label" htmlFor="companyOrigin" style={{fontWeight: 'bold', alignSelf: 'baseline'}}>{common.companyOrigin}<span style={{color: 'red'}}>*</span></label>                   
                      <Field name="companyOrigin" as="select" className="form-control">
                        {companyOrigins?.map((i, index)=> (              
                          <option key={index} className="option-item" value={+i.id}>{feathersStore.translate('en', i.label , i.labelEnglish)}</option>
                        ))}             
                      </Field>
                      <ErrorMessage name="companyOrigin" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>                  
                  </div>
                  <div className="row-container">
                    <div className="three-quarters-container">                      
                      <label htmlFor="postalAddress" style={{fontWeight: 'bold'}}>{checkout.postalAddress}</label><span style={{color: 'red'}}>*</span>                      
                      <Field type="text" className="form-control" name="postalAddress" />
                      <ErrorMessage name="postalAddress" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>       
                    </div>
                    <div className="one-quarter-container-right">                     
                      <label htmlFor="postalAddressNo" style={{fontWeight: 'bold'}}>{checkout.postalAddressNo}</label><span style={{color: 'red'}}>*</span>                    
                      <Field name="postalAddressNo" type="text" className="form-control" />
                      <ErrorMessage name="postalAddressNo" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>                
                  </div> 
                  <div className="row-container">
                    <div className="three-quarters-container">                      
                      <label htmlFor="postalAreaDescription" style={{fontWeight: 'bold'}}>{checkout.postalAreaDescription}</label><span style={{color: 'red'}}>*</span>                      
                      <Field type="text" className="form-control" name="postalAreaDescription" />
                      <ErrorMessage name="postalAreaDescription" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>       
                    </div>
                    <div className="one-quarter-container-right">                     
                      <label htmlFor="postalZipCode" style={{fontWeight: 'bold'}}>{checkout.postalZipCode}</label><span style={{color: 'red'}}>*</span>                    
                      <Field name="postalZipCode" type="text" className="form-control" />
                      <ErrorMessage name="postalZipCode" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                   </div>     
                  <div className="row-container">
                    <div className="three-quarters-container">                     
                      <label htmlFor="companyEmail" style={{fontWeight: 'bold'}}>{checkout.companyEmail}</label><span style={{color: 'red'}}>*</span>                    
                      <Field name="companyEmail" type="text" className="form-control" />
                      <ErrorMessage name="companyEmail" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div> 
                    <div className="one-quarter-container-right">                      
                      <label htmlFor="companyPhone" style={{fontWeight: 'bold'}}>{checkout.companyPhone}</label><span style={{color: 'red'}}>*</span>                      
                      <Field type="text" className="form-control" name="companyPhone" />
                      <ErrorMessage name="companyPhone" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>       
                    </div>                                
                  </div>
                  <div>
                    <div className="company-addresses-container">
                      <div className="company-address-container"> 
                        <FormControl component="fieldset" style={{width: '100%'}}>
                          <FormLabel component="legend"/>
                          <RadioGroup  name="addresses"  onChange={setDeliveryAddress}>
                            {
                            addresses?.length > 0 && addresses.map(renderAddressItem)
                            }
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </div>
                      <div className="addAddressOptionsButton">
                      <div className="addAddressOptionsButton-label">{common.addNewAddress}</div>
                      <RoundedButton
                        aria-label='Open new address'
                        color={'#00b970'}
                        icon={ <AddIcon style={{color: '#fff', fontSize: 30, marginTop: 2}}/>}
                        onClick={openNewAddress}
                      />
                    </div>
                  </div>  
                  <AddOrEditaddressModal 
                    visible={visibleAddNewAddress} 
                    close={closeNewAddress} 
                    itemToEdit={{}}
                    companyId={companyIdRef.current}
                    setAddresses={setAddresses}
                  />                           
                </div>}
              </SlideDown> 
              {feathersStore.tableId === 'eshop' &&
              <>
              { !formik.values.isCompany && !feathersStore.settings?.reducedCheckoutData &&
                <div className="checkbox-container">
                  <Field type="checkbox"   name="hasOtherAddress"
                      style={{width: 16, height: 16}}/>
                  <label className="form-check-label" htmlFor="hasOtherAddress" >
                    {checkout.differentDeliveryAddress}
                  </label>
                </div>
              }
              <SlideDown className='my-dropdown-slidedown'>
                {formik.values.hasOtherAddress && 
                <div style={{marginTop: 20}}>
                  <div className="row-container">
                    <div className="two-quarters-container">                      
                      <label htmlFor="deliveryFirstName" style={{fontWeight: 'bold'}}>{common.firstName}</label><span style={{color: 'red'}}>*</span>                      
                      <Field type="text" className="form-control" name="deliveryFirstName" />
                      <ErrorMessage name="deliveryFirstName" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>       
                    </div>
                    <div className="two-quarters-container-right">                     
                      <label htmlFor="deliveryLastName" style={{fontWeight: 'bold'}}>{common.lastName}</label><span style={{color: 'red'}}>*</span>                    
                      <Field name="deliveryLastName" type="text" className="form-control" />
                      <ErrorMessage name="deliveryLastName" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>                
                  </div>
                  <div className="row-container">                    
                    <div className="three-quarters-container">
                      <label style={{fontWeight: 'bold'}} htmlFor="deliveryStreet">{common.street}</label><span style={{color: 'red'}}>*</span>                      
                      <Field name="deliveryStreet" type="text" className="form-control" />
                      <ErrorMessage name="deliveryStreet" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>                   
                    <div className="one-quarter-container-right">                
                      <label style={{fontWeight: 'bold'}} htmlFor="deliveryStreetNumber">{common.streetNumber}</label><span style={{color: 'red'}}>*</span>               
                      <Field name="deliveryStreetNumber" type="text" className="form-control" />
                      <ErrorMessage name="deliveryStreetNumber" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                  </div>
                  <div>
                    <div className="row-container">
                      <label style={{fontWeight: 'bold'}} htmlFor="deliveryCity">{common.city}</label><span style={{color: 'red'}}>*</span>
                    </div>
                    <Field name="deliveryCity" as="select" className="form-control">
                      <option className="option-item" value={-1}>{common.selectSmall}</option>
                      {feathersStore?.settings?.citys?.filter(c => c.enabled)?.map((i, index)=> (              
                        <option key={index} className="option-item" value={+i.id}>{feathersStore._translate(i.name , i.nameEnglish)}</option>
                      ))}             
                    </Field>
                    <ErrorMessage name="deliveryCity" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                  </div>
                  <div className="row-container">
                    <div className="three-quarters-container">                      
                      <label htmlFor="deliveryOrigin" style={{fontWeight: 'bold'}}>{common.origin}</label><span style={{color: 'red'}}>*</span>                      
                      <Field name="deliveryOrigin" as="select" className="form-control" >
                        {origins?.map((i, index)=> (              
                          <option key={index} className="option-item" value={+i.id}>{feathersStore.translate('en', i.label , i.labelEnglish)}</option>
                        ))}             
                      </Field>
                      <ErrorMessage name="deliveryOrigin" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                    <div className="one-quarter-container-right">                      
                      <label htmlFor="deliveryPostcode" style={{fontWeight: 'bold'}}>{common.postcode}</label>                  
                      <Field name="deliveryPostcode" type="text" className="form-control" />
                      <ErrorMessage name="deliveryPostcode" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                    </div>
                  </div>
                  <div>
                    <div className="row-container">
                      <label htmlFor="deliveryPhone" style={{fontWeight: 'bold'}}>{common.phone}</label><span style={{color: 'red'}}>*</span>
                    </div>
                    <Field name="deliveryPhone" type="text" className="form-control" />
                    <ErrorMessage name="deliveryPhone" >{msg => <div className="error-message">{msg}</div>}</ErrorMessage>
                  </div>
                </div>
                }
              </SlideDown>
              </>
              }
              <div style={{display: 'flex', flexFlow: 'column', alignItems: 'baseline', justifyContent: 'left', marginTop: 20, width: '100%'}}>
                <label style={{fontWeight: 'bold'}} htmlFor="comments">
                  {checkout.orderComments}
                </label>
                <Field as="textarea" className="form-control" name="comments" rows="3"/>
              </div>
              {feathersStore.settings?.deferredOrdering && feathersStore.tableId === 'eshop' &&
                <>
                  <div className="checkbox-container" style={{marginTop: 20}}>
                    <Field type="checkbox" name="deferredOrder" style={{width: 16, height: 16}}/>
                    <label className="form-check-label" htmlFor="deferredOrder">
                      {formik.values.orderKind === 'ClickAway' ? checkout.scheduledPickupTime : checkout.scheduledDeliveryTime}
                    </label>
                  </div>
                  <SlideDown className='my-dropdown-slidedown'>
                    {formik.values.deferredOrder &&
                      <div style={{display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap'}}>
                        <DatePicker
                          timezone={feathersStore.timezone()}
                          label={common.date}
                          value={formik.values.deferredDate}
                          onChange={val => {
                            formik.setFieldValue('deferredDate', val);
                            formik.setFieldValue('deferredTime', null);
                          }}
                          disablePast
                          shouldDisableDate={date => {
                            const dayData = getDaySchedule(date);
                            return !dayData || dayData.hoursEnabled;
                          }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              error: !!(formik.touched.deferredDate && formik.errors.deferredDate),
                              helperText: formik.touched.deferredDate && formik.errors.deferredDate
                            }
                          }}
                        />
                        <TimePicker
                          timezone={feathersStore.timezone()}
                          ampm={false}
                          label={common.hour}
                          value={formik.values.deferredTime}
                          onChange={val => formik.setFieldValue('deferredTime', val)}
                          minTime={getTimeConstraints(formik.values.deferredDate).minTime}
                          maxTime={getTimeConstraints(formik.values.deferredDate).maxTime}
                          slotProps={{
                            textField: {
                              size: 'small',
                              error: !!(formik.touched.deferredTime && formik.errors.deferredTime),
                              helperText: formik.touched.deferredTime && formik.errors.deferredTime
                            }
                          }}
                        />
                      </div>
                    }
                  </SlideDown>
                </>
              }
            </div>
            <div className="right-container">
              <div className="checkout-order-table-title">{checkout.yourOrder}</div>
              <div className="checkout-order-table">
                <div className="checkout-order-table-header">
                  <span >{checkout.product}</span>
                  <span className="sub-total-color">{checkout.subTotal}</span>
                </div>              
                {renderCartItems}  
                {!(feathersStore.settings?.reducedCheckoutData && +_t.transportation === 0) &&
                <div className="checkout-order-table-header">
                  <div>{common.transportation}</div>
                  <Field name="transportation"
                    type="number"
                    value={_t.transportation}
                    component={transportationComponent}>
                  </Field>
                </div>}
                {(+_t.couponDiscount > 0) && <div className="checkout-order-table-header">
                  <div>{common.couponDiscount} {voucherDiscount.type === "Percent" ? voucherDiscount.reward + "%" : ""} </div>
                  <Field name="couponDiscount" 
                    type="number" 
                    value={_t.couponDiscount}
                    component={discountComponent}>             
                  </Field>
                </div> } 
                {(loyaltyReward > 0) && <div className="checkout-order-table-header">
                  <div>{common.loyaltyTitle} {feathersStore.formatMoney(parse_fix(loyaltyReward))} </div>
                  <Field name="loyaltyDiscount" 
                    type="number" 
                    value={_t.loyaltyDiscount}
                    component={discountComponent}>             
                  </Field>
                </div> } 
                {(discount > 0) && <div className="checkout-order-table-header">
                  <div>{common.customerDiscount} </div>
                  <Field name="customerDiscount" 
                    type="number" 
                    value={_t.customerDiscount}
                    component={discountComponent}>             
                  </Field>
                </div> }         
                <div className="checkout-order-table-header">
                  <div style={{fontWeight: 'bold'}}>{checkout.total}</div>
                  <Field name="grandTotal"
                    type="number"
                    value={_t.grandTotal}
                    component={grandTotalComponent}>
                  </Field>
                </div> 
                {feathersStore?.tableId !== 'eshop' && liveTableItems?.length > 0 &&
                  <>
                    <div className="payment-title">{checkout.alreadyOnTable || "Already on this table"}</div>
                    <div style={{fontSize: '0.8rem', opacity: 0.7, marginBottom: 6, paddingLeft: 4}}>
                      {checkout.notChargedToYou || "Already ordered - not added to your payment."}
                    </div>
                    {renderExistingTableItems}
                  </>
                }
                {feathersStore?.tableId === 'eshop' &&
                  <>
                    <div className="payment-title">{checkout.orderKind}</div> 
                    {!feathersStore.settings?.disableDelivery &&
                      <div className="checkout-order-table-paymentType">
                        <Field type="radio" name="orderKind" value="Delivery" />
                        <span style={{marginLeft: 5}}>{checkout.delivery}</span>                  
                      </div>
                    }
                    {!feathersStore.settings?.disableClickAway &&
                      <div className="checkout-order-table-paymentType">
                        <Field type="radio" name="orderKind" value="ClickAway" />
                        <span style={{marginLeft: 5}}>{checkout.clickAway}</span>                  
                      </div>
                    }
                  </> 
                }
                <div className="payment-title">{checkout.payment}</div>  
                { ((feathersStore?.tableId === 'eshop' && !feathersStore.settings?.disableCashPayments) ||
                 (feathersStore?.tableId !== 'eshop' && !feathersStore.settings?.disableOandPCashPayments)) &&
                  <div className="checkout-order-table-paymentType">
                    <Field type="radio" name="paymentMethod" value="Cash" />
                    <span style={{marginLeft: 5}}>
                      {formik.values.orderKind !== "ClickAway" ? checkout.cash : checkout.cashInShop}
                    </span>                  
                  </div>
                }
                {feathersStore?.tableId === 'eshop' && 
                  <>               
                    {(feathersStore.settings?.bankAccounts?.filter(item => item.bankEnabled)?.length > 0) &&
                      <div className="checkout-order-table-paymentType">
                        <Field type="radio" name="paymentMethod" value="BankTransfer" />
                        <span style={{marginLeft: 5}}>{checkout.bankTransfer}</span>                  
                      </div>  
                    }                         
                    {formik.values.paymentMethod === "BankTransfer" && 
                      <div className="banks-container column-flex-center">
                        <div className="bank-table-container">
                          <BanksIterator/>
                        </div>
                        <p>
                          {checkout.bankLargeText}
                        </p>
                      </div>
                    }   
                  </> 
                }    
                { ((feathersStore?.tableId === 'eshop' && feathersStore.settings?.hasCardPayments) ||
                  (feathersStore?.tableId !== 'eshop' && feathersStore.settings?.hasOandPCardPayments)) &&                
                  <>                       
                    <div className="checkout-order-table-paymentType">
                      <Field type="radio" name="paymentMethod"  value="PayPal"/>
                      <span style={{marginLeft: 5}}>{vivaSmart ? checkout.vivaRadio : (STRIPE_PUBLISHABLE_KEY ? checkout.stripeRadio || "Card (Stripe)" : checkout.paypalRadio)}</span>
                    </div>                 
                    <div
                      hidden={formik.values.paymentMethod !== "PayPal" || formik.isValid} 
                      className="paypal-error"
                    >
                      {checkout.paypalError}
                    </div>
                   
                    { feathersStore.settings?.ALLOW_RECURRING && 
                      formik.values?.paymentMethod === "PayPal" &&
                      feathersStore?.user?.email !== DEFAULT_EMAIL &&
                      <div>
                        <div className="checkout-order-table-acceptTerms">
                          <Field type="checkbox" name="allowRecurring" />
                          <span style={{ marginLeft: 5}}>{checkout.allowRecurring}</span>
                        </div>
                        <div className="checkout-order-table-acceptTerms recurring-wrapper">
                          <div style={{ marginRight: 15, marginLeft: 5}}>{checkout.subscriptionInterval}</div>
                          <Field name="subscriptionInterval" as="select" className="form-control recurring-select" >
                            {subscriptionIntervalsRef.current?.map((i, index)=> (              
                              <option key={index} className="option-item" value={i}>{i}</option>
                            ))}             
                          </Field>
                        </div>
                      </div>
                    }
                  </>                
                } 
                <div className="checkout-order-table-acceptTerms">
                  <span>
                  <Field type="checkbox" name="acceptedTerms" />
                  <span style={{ marginLeft: 5}}>
                  {checkout.iHaveRead} <a href="#" onClick={goToTerms}>{common.termsOfService}</a> 
                    &nbsp;{checkout.ofThisSite}<span style={{color: 'red'}}>*</span></span></span>
                </div> 
              

              </div>  
              <div className="checkout-order-table-submit">            
                {formik.values.paymentMethod === 'Cash' 
                  || formik.values.paymentMethod === 'BankTransfer' ? 
                <GreenSubmitButton 
                  type="submit"
                  aria-label='Submit' 
                  width="70%"
                  disabled={formik.isSubmitting || !formik.values.acceptedTerms || !formik.isValid }
                  title={checkout.sendOrder}
                  color={undefined}
                ></GreenSubmitButton>
                :
                vivaSmart &&
                  <GreenSubmitButton 
                    onClick={createVivaOrder}
                    loading={vivaPaymentOrdering}
                    aria-label='Viva-btn' 
                    width="70%"
                    disabled={formik.isSubmitting || !formik.values.acceptedTerms || !formik.isValid }
                    title={checkout.proceedToPayment}
                    color={undefined}
                  ></GreenSubmitButton>
                } 
              </div>            
            </div>
          </div>
        </div> 
        <div style={{ display: formState.paymentMethod === 'PayPal' && !vivaSmart && !STRIPE_PUBLISHABLE_KEY ? 'flex' : 'none' }}>
          <PayPalSection
            createOrder={createOrder}
            onApprove={approved}
            onClick={onClick}
            disabled={!formik.isValid || !formik.values.acceptedTerms}
          />
        </div>
        {formState.paymentMethod === 'PayPal' && !vivaSmart && !!STRIPE_PUBLISHABLE_KEY && stripePromise &&
          <Elements stripe={stripePromise}>
            <StripeCardForm
              onPay={handleStripePay}
              loading={stripeLoading}
              disabled={!formik?.isValid || !formik.values.acceptedTerms}
            />
          </Elements>
        }
      </Form>
      )}}
      

      </Formik>      
        <TipModal
          cashToPay={computeTotals(formRef.current?.values || {}).grandTotal}
          onButtonPress={applyTipAmount}
          onClosePress={closeTipModal}
          visible={tipModal}
        />
        <SmallAlert
          show={errorAlert.show}
          mode="error"
          message={errorAlert.message}
          onClose={() => setErrorAlert({ show: false, message: '' })}
        />
      </>
    <div className="checkout-order-table-footer"/>      
   
  </>
  )
}

export default inject('feathersStore')(observer(Checkout));